import {
  cookedFromMoisture,
  ingredientNutrients,
  scaleNutrients,
  sumNutrients,
  type Food,
} from '@/domain/nutrition/calc';
import type { FoodCategory, Ingredient, Meal, Region, UnitId } from '@/domain/nutrition/types';
import type { CustomFood } from '@/domain/models/schemas';
import { INGREDIENTS, type IngredientId } from './ingredients';
import { withMeasures } from './measures';

/**
 * The built-in Indian food library: standard home recipes in grams of raw ingredients, with
 * every value calculated from USDA SR28 ingredient values (`ingredients.ts`). Nothing here is
 * copied from another app or database. Recipes are written for a home kitchen with a normal
 * amount of oil; the oil level in the logger changes the fat named in `fat`.
 *
 * Cooked weights: stated for curries, dals and rice (the pot after cooking), or worked out
 * from the moisture of the cooked food for breads and batters (USDA chapati and naan hold
 * about 33 to 35% water, so breads use 34%).
 *
 * Bump FOOD_LIBRARY_VERSION whenever a value changes; logs keep the numbers they were saved
 * with, so improving a recipe never rewrites anyone's history.
 */
export const FOOD_LIBRARY_VERSION = 1;

export type Line = [IngredientId, number];

export interface DishDef {
  id: string;
  name: string;
  aliases?: string[];
  category: FoodCategory;
  region?: Region;
  meals: Meal[];
  lines: Line[];
  /** Ingredients that are the cooking fat, changed by the oil level. */
  fat?: IngredientId[];
  /** Grams of the whole pot after cooking, or the moisture of the cooked food. */
  cooked: number | { moisture: number };
  pieces?: number;
  piece?: [string, string];
  servings?: number;
  units: UnitId[];
  amount?: number;
  density?: number;
}

interface BasicDef {
  id: string;
  name: string;
  aliases?: string[];
  category: FoodCategory;
  region?: Region;
  meals: Meal[];
  ingredient: IngredientId;
  units: UnitId[];
  pieceG?: number;
  piece?: [string, string];
  amount?: number;
  density?: number;
}

const BREAD = { moisture: 0.34 };
const ALL_MEALS: Meal[] = ['breakfast', 'lunch', 'snacks', 'dinner'];
const MAINS: Meal[] = ['lunch', 'dinner'];
const CURRY: UnitId[] = ['katori', 'ladle', 'g'];
const DRY: UnitId[] = ['katori', 'g'];
const RICE: UnitId[] = ['katori', 'serving', 'g'];
const PIECES: UnitId[] = ['piece', 'g'];

const DISHES: DishDef[] = [
  // Breads ----------------------------------------------------------------------------------
  {
    id: 'phulka',
    name: 'Phulka',
    aliases: ['thin roti', 'fulka', 'chapati'],
    category: 'bread',
    meals: MAINS,
    lines: [['atta', 20]],
    cooked: BREAD,
    pieces: 1,
    piece: ['phulka', 'phulkas'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'roti',
    name: 'Roti',
    aliases: ['chapati', 'chapatti', 'rotli', 'fulka'],
    category: 'bread',
    meals: MAINS,
    lines: [['atta', 30]],
    cooked: BREAD,
    pieces: 1,
    piece: ['roti', 'rotis'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'tandoori-roti',
    name: 'Tandoori roti',
    aliases: ['thick roti'],
    category: 'bread',
    meals: MAINS,
    lines: [['atta', 45]],
    cooked: BREAD,
    pieces: 1,
    piece: ['roti', 'rotis'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'ghee-roti',
    name: 'Roti with ghee',
    aliases: ['chapati ghee', 'chupadi roti'],
    category: 'bread',
    meals: MAINS,
    lines: [
      ['atta', 30],
      ['ghee', 3],
    ],
    fat: ['ghee'],
    cooked: BREAD,
    pieces: 1,
    piece: ['roti', 'rotis'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'tawa-paratha',
    name: 'Plain paratha',
    aliases: ['tawa paratha', 'lachha paratha', 'parantha'],
    category: 'bread',
    meals: ['breakfast', 'lunch', 'dinner'],
    lines: [
      ['atta', 40],
      ['ghee', 6],
    ],
    fat: ['ghee'],
    cooked: { moisture: 0.3 },
    pieces: 1,
    piece: ['paratha', 'parathas'],
    units: PIECES,
  },
  {
    id: 'aloo-paratha',
    name: 'Aloo paratha',
    aliases: ['potato paratha', 'aloo parantha'],
    category: 'bread',
    meals: ['breakfast', 'lunch'],
    lines: [
      ['atta', 45],
      ['potato', 70],
      ['onion', 5],
      ['green-chilli', 2],
      ['ghee', 8],
    ],
    fat: ['ghee'],
    cooked: 125,
    pieces: 1,
    piece: ['paratha', 'parathas'],
    units: PIECES,
  },
  {
    id: 'gobhi-paratha',
    name: 'Gobhi paratha',
    aliases: ['cauliflower paratha', 'gobi paratha'],
    category: 'bread',
    meals: ['breakfast', 'lunch'],
    lines: [
      ['atta', 45],
      ['cauliflower', 60],
      ['green-chilli', 2],
      ['ghee', 8],
    ],
    fat: ['ghee'],
    cooked: 110,
    pieces: 1,
    piece: ['paratha', 'parathas'],
    units: PIECES,
  },
  {
    id: 'paneer-paratha',
    name: 'Paneer paratha',
    aliases: ['paneer parantha'],
    category: 'bread',
    meals: ['breakfast', 'lunch'],
    lines: [
      ['atta', 45],
      ['paneer', 40],
      ['onion', 5],
      ['ghee', 8],
    ],
    fat: ['ghee'],
    cooked: 110,
    pieces: 1,
    piece: ['paratha', 'parathas'],
    units: PIECES,
  },
  {
    id: 'puri',
    name: 'Puri',
    aliases: ['poori'],
    category: 'bread',
    meals: ['breakfast', 'lunch'],
    lines: [
      ['atta', 20],
      ['oil', 5],
    ],
    fat: ['oil'],
    cooked: { moisture: 0.25 },
    pieces: 1,
    piece: ['puri', 'puris'],
    units: PIECES,
    amount: 3,
  },
  {
    id: 'bhatura',
    name: 'Bhatura',
    aliases: ['bhature'],
    category: 'bread',
    meals: ['breakfast', 'lunch'],
    lines: [
      ['maida', 55],
      ['curd', 12],
      ['oil', 12],
    ],
    fat: ['oil'],
    cooked: { moisture: 0.28 },
    pieces: 1,
    piece: ['bhatura', 'bhaturas'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'naan',
    name: 'Butter naan',
    aliases: ['naan', 'nan'],
    category: 'bread',
    meals: MAINS,
    lines: [
      ['maida', 75],
      ['curd', 12],
      ['butter', 6],
    ],
    fat: ['butter'],
    cooked: BREAD,
    pieces: 1,
    piece: ['naan', 'naans'],
    units: PIECES,
  },
  {
    id: 'makki-roti',
    name: 'Makki ki roti',
    aliases: ['makki roti', 'maize roti', 'corn roti'],
    category: 'bread',
    meals: MAINS,
    lines: [
      ['makki-atta', 45],
      ['ghee', 5],
    ],
    fat: ['ghee'],
    cooked: BREAD,
    pieces: 1,
    piece: ['roti', 'rotis'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'bajra-roti',
    name: 'Bajra roti',
    aliases: ['bajre ki roti', 'bajra bhakri', 'millet roti'],
    category: 'bread',
    region: 'west',
    meals: MAINS,
    lines: [['bajra', 40]],
    cooked: BREAD,
    pieces: 1,
    piece: ['roti', 'rotis'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'jowar-bhakri',
    name: 'Jowar bhakri',
    aliases: ['jowar roti', 'jolada rotti'],
    category: 'bread',
    region: 'west',
    meals: MAINS,
    lines: [['jowar', 40]],
    cooked: BREAD,
    pieces: 1,
    piece: ['bhakri', 'bhakris'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'thepla',
    name: 'Methi thepla',
    aliases: ['thepla'],
    category: 'bread',
    region: 'west',
    meals: ['breakfast', 'lunch', 'snacks'],
    lines: [
      ['atta', 35],
      ['besan', 5],
      ['curd', 5],
      ['oil', 4],
    ],
    fat: ['oil'],
    cooked: { moisture: 0.28 },
    pieces: 1,
    piece: ['thepla', 'theplas'],
    units: PIECES,
    amount: 2,
  },

  // Rice ------------------------------------------------------------------------------------
  // Cooked rice weighs 2.8 times the raw rice (USDA water: 11.6% raw, 68.4% boiled).
  {
    id: 'rice',
    name: 'Steamed rice',
    aliases: ['rice', 'chawal', 'plain rice', 'boiled rice', 'bhaat', 'sadam', 'annam'],
    category: 'rice',
    region: 'all',
    meals: MAINS,
    lines: [['rice', 100]],
    cooked: 280,
    servings: 2,
    units: RICE,
    density: 0.66,
  },
  {
    id: 'jeera-rice',
    name: 'Jeera rice',
    aliases: ['cumin rice'],
    category: 'rice',
    meals: MAINS,
    lines: [
      ['rice', 200],
      ['ghee', 12],
      ['cumin', 3],
    ],
    fat: ['ghee'],
    cooked: 575,
    servings: 4,
    units: RICE,
    density: 0.66,
  },
  {
    id: 'veg-pulao',
    name: 'Veg pulao',
    aliases: ['pulav', 'pilaf', 'vegetable pulao'],
    category: 'rice',
    meals: MAINS,
    lines: [
      ['rice', 200],
      ['peas', 50],
      ['carrot', 40],
      ['beans', 30],
      ['onion', 40],
      ['ghee', 20],
    ],
    fat: ['ghee'],
    cooked: 720,
    servings: 4,
    units: RICE,
    density: 0.68,
  },
  {
    id: 'veg-biryani',
    name: 'Veg biryani',
    aliases: ['vegetable biryani', 'biriyani'],
    category: 'rice',
    meals: MAINS,
    lines: [
      ['rice', 200],
      ['potato', 60],
      ['carrot', 50],
      ['beans', 40],
      ['peas', 50],
      ['onion', 80],
      ['curd', 50],
      ['oil', 25],
      ['ghee', 8],
    ],
    fat: ['oil', 'ghee'],
    cooked: 850,
    servings: 4,
    units: RICE,
    density: 0.7,
  },
  {
    id: 'chicken-biryani',
    name: 'Chicken biryani',
    aliases: ['murgh biryani', 'biriyani'],
    category: 'chicken',
    meals: MAINS,
    lines: [
      ['rice', 250],
      ['chicken', 350],
      ['onion', 150],
      ['curd', 100],
      ['tomato', 60],
      ['oil', 35],
      ['ghee', 10],
    ],
    fat: ['oil', 'ghee'],
    cooked: 1150,
    servings: 5,
    units: RICE,
    density: 0.72,
  },
  {
    id: 'mutton-biryani',
    name: 'Mutton biryani',
    aliases: ['gosht biryani'],
    category: 'meat',
    meals: MAINS,
    lines: [
      ['rice', 250],
      ['mutton', 350],
      ['onion', 150],
      ['curd', 100],
      ['tomato', 60],
      ['oil', 35],
      ['ghee', 10],
    ],
    fat: ['oil', 'ghee'],
    cooked: 1150,
    servings: 5,
    units: RICE,
    density: 0.72,
  },
  {
    id: 'khichdi',
    name: 'Moong dal khichdi',
    aliases: ['khichri', 'khichadi'],
    category: 'rice',
    region: 'all',
    meals: MAINS,
    lines: [
      ['rice', 100],
      ['moong-dal', 50],
      ['ghee', 10],
      ['turmeric', 1],
    ],
    fat: ['ghee'],
    cooked: 650,
    servings: 3,
    units: CURRY,
  },
  {
    id: 'curd-rice',
    name: 'Curd rice',
    aliases: ['thayir sadam', 'dahi chawal', 'mosaranna'],
    category: 'rice',
    region: 'south',
    meals: MAINS,
    lines: [
      ['rice', 100],
      ['curd', 200],
      ['milk', 50],
      ['oil', 5],
    ],
    fat: ['oil'],
    cooked: 535,
    servings: 3,
    units: RICE,
    density: 0.9,
  },
  {
    id: 'lemon-rice',
    name: 'Lemon rice',
    aliases: ['chitranna', 'elumichai sadam'],
    category: 'rice',
    region: 'south',
    meals: MAINS,
    lines: [
      ['rice', 150],
      ['peanuts', 20],
      ['lemon', 15],
      ['oil', 15],
      ['turmeric', 1],
    ],
    fat: ['oil'],
    cooked: 470,
    servings: 3,
    units: RICE,
    density: 0.66,
  },

  // Dals ------------------------------------------------------------------------------------
  {
    id: 'dal-tadka',
    name: 'Dal tadka',
    aliases: ['dal', 'daal', 'dhal', 'toor dal', 'arhar dal', 'tadka dal', 'yellow dal'],
    category: 'dal',
    region: 'all',
    meals: MAINS,
    lines: [
      ['toor-dal', 150],
      ['onion', 40],
      ['tomato', 60],
      ['ghee', 15],
      ['garlic', 5],
      ['cumin', 2],
      ['turmeric', 1],
      ['chilli-powder', 1],
    ],
    fat: ['ghee'],
    cooked: 800,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'dal-fry',
    name: 'Dal fry',
    aliases: ['daal fry'],
    category: 'dal',
    meals: MAINS,
    lines: [
      ['toor-dal', 150],
      ['onion', 60],
      ['tomato', 80],
      ['oil', 20],
      ['garlic', 5],
      ['cumin', 2],
      ['turmeric', 1],
    ],
    fat: ['oil'],
    cooked: 850,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'moong-dal',
    name: 'Moong dal',
    aliases: ['yellow moong dal', 'mung dal'],
    category: 'dal',
    region: 'all',
    meals: MAINS,
    lines: [
      ['moong-dal', 150],
      ['tomato', 40],
      ['ghee', 10],
      ['cumin', 2],
      ['turmeric', 1],
    ],
    fat: ['ghee'],
    cooked: 800,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'masoor-dal',
    name: 'Masoor dal',
    aliases: ['red lentil dal', 'lentil dal'],
    category: 'dal',
    region: 'all',
    meals: MAINS,
    lines: [
      ['masoor-dal', 150],
      ['onion', 40],
      ['tomato', 50],
      ['oil', 12],
      ['turmeric', 1],
    ],
    fat: ['oil'],
    cooked: 800,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'chana-dal',
    name: 'Chana dal',
    aliases: ['chane ki dal'],
    category: 'dal',
    meals: MAINS,
    lines: [
      ['chana-dal', 150],
      ['onion', 40],
      ['tomato', 50],
      ['oil', 15],
      ['turmeric', 1],
    ],
    fat: ['oil'],
    cooked: 750,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'dal-makhani',
    name: 'Dal makhani',
    aliases: ['maa ki dal', 'kali dal'],
    category: 'dal',
    meals: MAINS,
    lines: [
      ['urad-dal', 100],
      ['rajma', 25],
      ['tomato', 120],
      ['onion', 40],
      ['butter', 25],
      ['cream', 40],
      ['garlic', 8],
      ['ginger', 8],
    ],
    fat: ['butter'],
    cooked: 800,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'dal-palak',
    name: 'Dal palak',
    aliases: ['palak dal', 'spinach dal'],
    category: 'dal',
    meals: MAINS,
    lines: [
      ['toor-dal', 150],
      ['spinach', 150],
      ['tomato', 40],
      ['ghee', 15],
      ['garlic', 5],
    ],
    fat: ['ghee'],
    cooked: 900,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'rajma',
    name: 'Rajma',
    aliases: ['rajma masala', 'kidney beans curry'],
    category: 'dal',
    meals: MAINS,
    lines: [
      ['rajma', 150],
      ['onion', 100],
      ['tomato', 150],
      ['oil', 20],
      ['ginger', 8],
      ['garlic', 8],
      ['coriander-powder', 3],
      ['chilli-powder', 2],
    ],
    fat: ['oil'],
    cooked: 900,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'chole',
    name: 'Chole',
    aliases: ['chana masala', 'chhole', 'chickpea curry', 'kabuli chana'],
    category: 'dal',
    meals: ['breakfast', 'lunch', 'dinner'],
    lines: [
      ['kabuli-chana', 150],
      ['onion', 100],
      ['tomato', 150],
      ['oil', 25],
      ['ginger', 8],
      ['coriander-powder', 3],
      ['chilli-powder', 2],
    ],
    fat: ['oil'],
    cooked: 900,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'kadhi',
    name: 'Kadhi pakora',
    aliases: ['kadhi', 'kadi', 'punjabi kadhi'],
    category: 'dal',
    meals: MAINS,
    lines: [
      ['curd', 250],
      ['besan', 80],
      ['onion', 30],
      ['oil', 25],
      ['turmeric', 1],
    ],
    fat: ['oil'],
    cooked: 900,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'sambar',
    name: 'Sambar',
    aliases: ['sambhar', 'saambar'],
    category: 'south',
    region: 'south',
    meals: ['breakfast', 'lunch', 'dinner'],
    lines: [
      ['toor-dal', 75],
      ['pumpkin', 50],
      ['brinjal', 50],
      ['carrot', 40],
      ['onion', 60],
      ['tomato', 50],
      ['tamarind', 10],
      ['oil', 10],
      ['coriander-powder', 8],
      ['chilli-powder', 3],
    ],
    fat: ['oil'],
    cooked: 900,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'rasam',
    name: 'Rasam',
    aliases: ['saaru', 'chaaru'],
    category: 'south',
    region: 'south',
    meals: MAINS,
    lines: [
      ['tomato', 150],
      ['toor-dal', 20],
      ['tamarind', 10],
      ['ghee', 5],
      ['cumin', 2],
    ],
    fat: ['ghee'],
    cooked: 800,
    servings: 4,
    units: ['katori', 'glass', 'g'],
  },

  // Sabzi -----------------------------------------------------------------------------------
  {
    id: 'aloo-gobhi',
    name: 'Aloo gobhi',
    aliases: ['aloo gobi', 'potato cauliflower'],
    category: 'sabzi',
    meals: MAINS,
    lines: [
      ['potato', 250],
      ['cauliflower', 300],
      ['onion', 60],
      ['tomato', 60],
      ['oil', 25],
      ['turmeric', 1],
      ['coriander-powder', 3],
    ],
    fat: ['oil'],
    cooked: 600,
    servings: 4,
    units: DRY,
    density: 0.65,
  },
  {
    id: 'aloo-matar',
    name: 'Aloo matar',
    aliases: ['aloo mutter', 'potato peas curry'],
    category: 'sabzi',
    meals: MAINS,
    lines: [
      ['potato', 250],
      ['peas', 150],
      ['onion', 60],
      ['tomato', 120],
      ['oil', 20],
    ],
    fat: ['oil'],
    cooked: 800,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'aloo-sabzi',
    name: 'Aloo sabzi',
    aliases: ['aloo ki sabzi', 'potato curry', 'aloo rassa'],
    category: 'sabzi',
    meals: ['breakfast', 'lunch', 'dinner'],
    lines: [
      ['potato', 400],
      ['tomato', 150],
      ['oil', 20],
      ['cumin', 2],
      ['turmeric', 1],
    ],
    fat: ['oil'],
    cooked: 750,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'jeera-aloo',
    name: 'Jeera aloo',
    aliases: ['cumin potatoes', 'dry aloo'],
    category: 'sabzi',
    meals: MAINS,
    lines: [
      ['potato', 500],
      ['oil', 20],
      ['cumin', 4],
    ],
    fat: ['oil'],
    cooked: 480,
    servings: 4,
    units: DRY,
    density: 0.65,
  },
  {
    id: 'bhindi',
    name: 'Bhindi masala',
    aliases: ['bhindi', 'okra', 'bhendi', 'vendakkai'],
    category: 'sabzi',
    meals: MAINS,
    lines: [
      ['okra', 400],
      ['onion', 80],
      ['tomato', 40],
      ['oil', 30],
      ['coriander-powder', 3],
    ],
    fat: ['oil'],
    cooked: 400,
    servings: 4,
    units: DRY,
    density: 0.6,
  },
  {
    id: 'baingan-bharta',
    name: 'Baingan bharta',
    aliases: ['bharta', 'brinjal bharta', 'eggplant'],
    category: 'sabzi',
    meals: MAINS,
    lines: [
      ['brinjal', 600],
      ['onion', 80],
      ['tomato', 100],
      ['oil', 20],
      ['garlic', 5],
    ],
    fat: ['oil'],
    cooked: 550,
    servings: 4,
    units: DRY,
    density: 0.9,
  },
  {
    id: 'mix-veg',
    name: 'Mix veg',
    aliases: ['mixed vegetable', 'mixed veg sabzi'],
    category: 'sabzi',
    meals: MAINS,
    lines: [
      ['potato', 100],
      ['carrot', 100],
      ['beans', 100],
      ['peas', 100],
      ['cauliflower', 100],
      ['onion', 50],
      ['tomato', 80],
      ['oil', 25],
    ],
    fat: ['oil'],
    cooked: 600,
    servings: 4,
    units: DRY,
    density: 0.7,
  },
  {
    id: 'cabbage-sabzi',
    name: 'Patta gobhi sabzi',
    aliases: ['cabbage sabzi', 'band gobhi', 'cabbage poriyal'],
    category: 'sabzi',
    meals: MAINS,
    lines: [
      ['cabbage', 500],
      ['peas', 50],
      ['oil', 20],
      ['turmeric', 1],
    ],
    fat: ['oil'],
    cooked: 450,
    servings: 4,
    units: DRY,
    density: 0.6,
  },
  {
    id: 'lauki',
    name: 'Lauki sabzi',
    aliases: ['lauki', 'ghiya', 'bottle gourd', 'doodhi'],
    category: 'sabzi',
    meals: MAINS,
    lines: [
      ['lauki', 500],
      ['tomato', 60],
      ['oil', 12],
      ['turmeric', 1],
    ],
    fat: ['oil'],
    cooked: 450,
    servings: 4,
    units: DRY,
    density: 0.9,
  },
  {
    id: 'karela',
    name: 'Karela sabzi',
    aliases: ['karela', 'bitter gourd', 'pavakkai'],
    category: 'sabzi',
    meals: MAINS,
    lines: [
      ['karela', 300],
      ['onion', 100],
      ['oil', 25],
    ],
    fat: ['oil'],
    cooked: 250,
    servings: 3,
    units: DRY,
    density: 0.6,
  },
  {
    id: 'beans-poriyal',
    name: 'Beans poriyal',
    aliases: ['beans sabzi', 'french beans', 'beans thoran'],
    category: 'sabzi',
    region: 'south',
    meals: MAINS,
    lines: [
      ['beans', 400],
      ['coconut', 30],
      ['oil', 15],
    ],
    fat: ['oil'],
    cooked: 380,
    servings: 4,
    units: DRY,
    density: 0.6,
  },
  {
    id: 'sarson-saag',
    name: 'Sarson ka saag',
    aliases: ['saag', 'sarso da saag', 'mustard greens'],
    category: 'sabzi',
    meals: MAINS,
    lines: [
      ['sarson', 500],
      ['spinach', 200],
      ['makki-atta', 20],
      ['onion', 30],
      ['garlic', 10],
      ['ghee', 25],
    ],
    fat: ['ghee'],
    cooked: 600,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'mushroom-matar',
    name: 'Mushroom matar',
    aliases: ['mushroom curry', 'mushroom masala'],
    category: 'sabzi',
    meals: MAINS,
    lines: [
      ['mushroom', 250],
      ['peas', 150],
      ['onion', 80],
      ['tomato', 120],
      ['oil', 20],
    ],
    fat: ['oil'],
    cooked: 550,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'kaddu',
    name: 'Kaddu sabzi',
    aliases: ['pumpkin sabzi', 'khatta meetha kaddu', 'sitaphal'],
    category: 'sabzi',
    meals: MAINS,
    lines: [
      ['pumpkin', 500],
      ['oil', 15],
      ['jaggery', 10],
    ],
    fat: ['oil'],
    cooked: 450,
    servings: 4,
    units: DRY,
    density: 0.9,
  },
  {
    id: 'soya-curry',
    name: 'Soya chunks curry',
    aliases: ['soya chunks', 'nutrela', 'meal maker', 'soya bean curry'],
    category: 'sabzi',
    meals: MAINS,
    lines: [
      ['soya-chunks', 100],
      ['onion', 80],
      ['tomato', 120],
      ['oil', 20],
      ['coriander-powder', 3],
    ],
    fat: ['oil'],
    cooked: 700,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'avial',
    name: 'Avial',
    aliases: ['aviyal'],
    category: 'south',
    region: 'south',
    meals: MAINS,
    lines: [
      ['carrot', 100],
      ['beans', 100],
      ['pumpkin', 100],
      ['potato', 100],
      ['coconut', 80],
      ['curd', 60],
      ['oil', 10],
    ],
    fat: ['oil'],
    cooked: 550,
    servings: 4,
    units: DRY,
    density: 0.85,
  },

  // Paneer ----------------------------------------------------------------------------------
  {
    id: 'palak-paneer',
    name: 'Palak paneer',
    aliases: ['saag paneer', 'spinach paneer'],
    category: 'paneer',
    meals: MAINS,
    lines: [
      ['spinach', 500],
      ['paneer', 200],
      ['onion', 60],
      ['tomato', 60],
      ['cream', 20],
      ['oil', 20],
    ],
    fat: ['oil'],
    cooked: 750,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'matar-paneer',
    name: 'Matar paneer',
    aliases: ['mutter paneer', 'peas paneer'],
    category: 'paneer',
    meals: MAINS,
    lines: [
      ['paneer', 200],
      ['peas', 150],
      ['onion', 100],
      ['tomato', 150],
      ['cream', 20],
      ['oil', 25],
    ],
    fat: ['oil'],
    cooked: 800,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'shahi-paneer',
    name: 'Shahi paneer',
    aliases: ['paneer korma'],
    category: 'paneer',
    meals: MAINS,
    lines: [
      ['paneer', 200],
      ['onion', 80],
      ['tomato', 120],
      ['cashews', 25],
      ['cream', 50],
      ['butter', 20],
    ],
    fat: ['butter'],
    cooked: 700,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'paneer-butter-masala',
    name: 'Paneer butter masala',
    aliases: ['paneer makhani', 'butter paneer'],
    category: 'paneer',
    meals: MAINS,
    lines: [
      ['paneer', 200],
      ['tomato', 250],
      ['onion', 50],
      ['cashews', 20],
      ['cream', 50],
      ['butter', 30],
    ],
    fat: ['butter'],
    cooked: 750,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'kadai-paneer',
    name: 'Kadai paneer',
    aliases: ['karahi paneer'],
    category: 'paneer',
    meals: MAINS,
    lines: [
      ['paneer', 200],
      ['capsicum', 120],
      ['onion', 100],
      ['tomato', 150],
      ['oil', 25],
      ['coriander-powder', 4],
    ],
    fat: ['oil'],
    cooked: 650,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'paneer-bhurji',
    name: 'Paneer bhurji',
    aliases: ['scrambled paneer'],
    category: 'paneer',
    meals: ALL_MEALS,
    lines: [
      ['paneer', 200],
      ['onion', 60],
      ['tomato', 60],
      ['capsicum', 30],
      ['oil', 15],
    ],
    fat: ['oil'],
    cooked: 330,
    servings: 3,
    units: DRY,
    density: 0.7,
  },
  {
    id: 'paneer-tikka',
    name: 'Paneer tikka',
    aliases: ['tikka paneer'],
    category: 'paneer',
    meals: ['lunch', 'snacks', 'dinner'],
    lines: [
      ['paneer', 250],
      ['curd', 50],
      ['capsicum', 60],
      ['onion', 60],
      ['oil', 12],
    ],
    fat: ['oil'],
    cooked: 380,
    pieces: 12,
    piece: ['piece', 'pieces'],
    units: PIECES,
    amount: 4,
  },

  // Eggs ------------------------------------------------------------------------------------
  {
    id: 'omelette',
    name: 'Masala omelette',
    aliases: ['omelet', 'anda omelette', 'egg omelette'],
    category: 'egg',
    region: 'all',
    meals: ['breakfast', 'snacks', 'dinner'],
    lines: [
      ['egg', 100],
      ['onion', 20],
      ['tomato', 10],
      ['green-chilli', 2],
      ['oil', 7],
    ],
    fat: ['oil'],
    cooked: 125,
    pieces: 1,
    piece: ['omelette (2 eggs)', 'omelettes (2 eggs each)'],
    units: PIECES,
  },
  {
    id: 'egg-bhurji',
    name: 'Egg bhurji',
    aliases: ['anda bhurji', 'scrambled eggs'],
    category: 'egg',
    region: 'all',
    meals: ['breakfast', 'snacks', 'dinner'],
    lines: [
      ['egg', 150],
      ['onion', 40],
      ['tomato', 40],
      ['oil', 10],
    ],
    fat: ['oil'],
    cooked: 210,
    servings: 2,
    units: ['serving', 'katori', 'g'],
    density: 0.7,
  },
  {
    id: 'egg-curry',
    name: 'Egg curry',
    aliases: ['anda curry', 'egg masala'],
    category: 'egg',
    region: 'all',
    meals: MAINS,
    lines: [
      ['egg', 300],
      ['onion', 120],
      ['tomato', 150],
      ['oil', 25],
      ['coriander-powder', 3],
    ],
    fat: ['oil'],
    cooked: 750,
    servings: 4,
    units: CURRY,
  },

  // Chicken, meat and fish ------------------------------------------------------------------
  {
    id: 'chicken-curry',
    name: 'Chicken curry',
    aliases: ['murgh curry', 'chicken masala', 'chicken gravy'],
    category: 'chicken',
    region: 'all',
    meals: MAINS,
    lines: [
      ['chicken', 500],
      ['onion', 150],
      ['tomato', 150],
      ['curd', 50],
      ['oil', 35],
      ['ginger', 10],
      ['garlic', 10],
      ['coriander-powder', 5],
    ],
    fat: ['oil'],
    cooked: 850,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'butter-chicken',
    name: 'Butter chicken',
    aliases: ['murgh makhani', 'chicken makhani'],
    category: 'chicken',
    meals: MAINS,
    lines: [
      ['chicken', 500],
      ['tomato', 300],
      ['cream', 100],
      ['cashews', 20],
      ['butter', 40],
    ],
    fat: ['butter'],
    cooked: 900,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'chicken-tikka',
    name: 'Chicken tikka',
    aliases: ['tandoori chicken', 'chicken kebab'],
    category: 'chicken',
    meals: ['lunch', 'snacks', 'dinner'],
    lines: [
      ['chicken', 500],
      ['curd', 100],
      ['lemon', 10],
      ['oil', 15],
    ],
    fat: ['oil'],
    cooked: 460,
    pieces: 16,
    piece: ['piece', 'pieces'],
    units: PIECES,
    amount: 4,
  },
  {
    id: 'chicken-plain',
    name: 'Chicken, cooked plain',
    aliases: ['grilled chicken', 'boiled chicken', 'chicken breast'],
    category: 'chicken',
    region: 'all',
    meals: MAINS,
    lines: [
      ['chicken', 100],
      ['oil', 3],
    ],
    fat: ['oil'],
    cooked: 78,
    servings: 1,
    units: ['g', 'serving'],
    amount: 150,
  },
  {
    id: 'mutton-curry',
    name: 'Mutton curry',
    aliases: ['mutton', 'gosht', 'rogan josh', 'goat curry'],
    category: 'meat',
    region: 'all',
    meals: MAINS,
    lines: [
      ['mutton', 500],
      ['onion', 200],
      ['tomato', 120],
      ['curd', 50],
      ['oil', 40],
      ['ginger', 10],
      ['garlic', 10],
    ],
    fat: ['oil'],
    cooked: 850,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'fish-curry',
    name: 'Fish curry',
    aliases: ['machher jhol', 'macher jhol', 'meen curry', 'fish masala'],
    category: 'fish',
    region: 'east',
    meals: MAINS,
    lines: [
      ['fish', 500],
      ['onion', 100],
      ['tomato', 120],
      ['mustard-oil', 30],
      ['turmeric', 2],
    ],
    fat: ['mustard-oil'],
    cooked: 800,
    servings: 4,
    units: CURRY,
  },
  {
    id: 'fish-fry',
    name: 'Fish fry',
    aliases: ['tawa fish', 'fried fish'],
    category: 'fish',
    region: 'all',
    meals: MAINS,
    lines: [
      ['fish', 400],
      ['rice-flour', 20],
      ['oil', 30],
      ['chilli-powder', 3],
    ],
    fat: ['oil'],
    cooked: 360,
    pieces: 8,
    piece: ['piece', 'pieces'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'prawn-curry',
    name: 'Prawn curry',
    aliases: ['jhinga curry', 'prawns masala', 'chingri'],
    category: 'fish',
    region: 'all',
    meals: MAINS,
    lines: [
      ['prawns', 400],
      ['coconut', 60],
      ['onion', 80],
      ['tomato', 80],
      ['oil', 25],
    ],
    fat: ['oil'],
    cooked: 650,
    servings: 4,
    units: CURRY,
  },

  // Breakfast -------------------------------------------------------------------------------
  {
    id: 'poha',
    name: 'Poha',
    aliases: ['kanda poha', 'aval', 'avalakki', 'chivda'],
    category: 'breakfast',
    region: 'west',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['poha', 150],
      ['onion', 60],
      ['potato', 40],
      ['peas', 30],
      ['peanuts', 20],
      ['lemon', 10],
      ['oil', 15],
    ],
    fat: ['oil'],
    cooked: 470,
    servings: 3,
    units: ['katori', 'serving', 'g'],
    density: 0.55,
  },
  {
    id: 'upma',
    name: 'Upma',
    aliases: ['rava upma', 'uppittu', 'sooji upma'],
    category: 'breakfast',
    region: 'south',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['sooji', 150],
      ['onion', 50],
      ['carrot', 30],
      ['peanuts', 10],
      ['ghee', 15],
    ],
    fat: ['ghee'],
    cooked: 650,
    servings: 4,
    units: ['katori', 'serving', 'g'],
    density: 0.85,
  },
  {
    id: 'vermicelli-upma',
    name: 'Vermicelli upma',
    aliases: ['semiya upma', 'seviyan namkeen'],
    category: 'breakfast',
    region: 'south',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['vermicelli', 100],
      ['onion', 40],
      ['carrot', 30],
      ['peas', 30],
      ['oil', 12],
    ],
    fat: ['oil'],
    cooked: 420,
    servings: 2,
    units: ['katori', 'serving', 'g'],
    density: 0.75,
  },
  {
    id: 'idli',
    name: 'Idli',
    aliases: ['idly', 'iddli'],
    category: 'south',
    region: 'south',
    meals: ['breakfast', 'snacks', 'dinner'],
    lines: [
      ['rice', 300],
      ['urad-dal', 100],
    ],
    cooked: { moisture: 0.6 },
    pieces: 22,
    piece: ['idli', 'idlis'],
    units: PIECES,
    amount: 3,
  },
  {
    id: 'dosa',
    name: 'Plain dosa',
    aliases: ['dosa', 'dosai', 'dose'],
    category: 'south',
    region: 'south',
    meals: ['breakfast', 'dinner'],
    lines: [
      ['rice', 300],
      ['urad-dal', 100],
      ['oil', 30],
    ],
    fat: ['oil'],
    cooked: { moisture: 0.5 },
    pieces: 10,
    piece: ['dosa', 'dosas'],
    units: PIECES,
  },
  {
    id: 'masala-dosa',
    name: 'Masala dosa',
    aliases: ['masale dose'],
    category: 'south',
    region: 'south',
    meals: ['breakfast', 'lunch', 'dinner'],
    lines: [
      ['rice', 300],
      ['urad-dal', 100],
      ['potato', 400],
      ['onion', 80],
      ['oil', 50],
    ],
    fat: ['oil'],
    cooked: 1250,
    pieces: 10,
    piece: ['dosa', 'dosas'],
    units: PIECES,
  },
  {
    id: 'uttapam',
    name: 'Uttapam',
    aliases: ['uthappam', 'onion uttapam'],
    category: 'south',
    region: 'south',
    meals: ['breakfast', 'dinner'],
    lines: [
      ['rice', 225],
      ['urad-dal', 75],
      ['onion', 60],
      ['tomato', 60],
      ['capsicum', 30],
      ['oil', 24],
    ],
    fat: ['oil'],
    cooked: 680,
    pieces: 6,
    piece: ['uttapam', 'uttapams'],
    units: PIECES,
  },
  {
    id: 'medu-vada',
    name: 'Medu vada',
    aliases: ['vada', 'uddina vade', 'ulundu vadai'],
    category: 'south',
    region: 'south',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['urad-dal', 200],
      ['oil', 40],
    ],
    fat: ['oil'],
    cooked: { moisture: 0.4 },
    pieces: 10,
    piece: ['vada', 'vadas'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'pongal',
    name: 'Ven pongal',
    aliases: ['pongal', 'khara pongal'],
    category: 'south',
    region: 'south',
    meals: ['breakfast'],
    lines: [
      ['rice', 100],
      ['moong-dal', 50],
      ['ghee', 25],
      ['cashews', 10],
    ],
    fat: ['ghee'],
    cooked: 650,
    servings: 3,
    units: ['katori', 'serving', 'g'],
  },
  {
    id: 'coconut-chutney',
    name: 'Coconut chutney',
    aliases: ['chutney', 'nariyal chutney', 'thengai chutney'],
    category: 'side',
    region: 'south',
    meals: ['breakfast', 'snacks', 'dinner'],
    lines: [
      ['coconut', 100],
      ['chana-dal', 15],
      ['green-chilli', 5],
      ['oil', 5],
      ['water', 50],
    ],
    fat: ['oil'],
    cooked: 175,
    servings: 4,
    units: ['tbsp', 'katori', 'g'],
    amount: 2,
  },
  {
    id: 'besan-chilla',
    name: 'Besan chilla',
    aliases: ['cheela', 'besan cheela', 'pudla'],
    category: 'breakfast',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['besan', 100],
      ['onion', 30],
      ['tomato', 30],
      ['oil', 10],
    ],
    fat: ['oil'],
    cooked: 300,
    pieces: 4,
    piece: ['chilla', 'chillas'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'moong-chilla',
    name: 'Moong dal chilla',
    aliases: ['moong cheela', 'pesarattu'],
    category: 'breakfast',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['moong-dal', 100],
      ['onion', 30],
      ['ginger', 5],
      ['oil', 10],
    ],
    fat: ['oil'],
    cooked: 320,
    pieces: 4,
    piece: ['chilla', 'chillas'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'oats-milk',
    name: 'Oats with milk',
    aliases: ['oats', 'oatmeal', 'porridge', 'daliya'],
    category: 'breakfast',
    region: 'all',
    meals: ['breakfast'],
    lines: [
      ['oats', 40],
      ['milk', 200],
      ['sugar', 5],
    ],
    cooked: 245,
    servings: 1,
    units: ['serving', 'katori', 'g'],
  },
  {
    id: 'sabudana-khichdi',
    name: 'Sabudana khichdi',
    aliases: ['sago khichdi', 'vrat khichdi'],
    category: 'breakfast',
    region: 'west',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['sabudana', 150],
      ['potato', 100],
      ['peanuts', 50],
      ['ghee', 15],
    ],
    fat: ['ghee'],
    cooked: 480,
    servings: 3,
    units: ['katori', 'serving', 'g'],
    density: 0.75,
  },
  {
    id: 'dhokla',
    name: 'Dhokla',
    aliases: ['khaman', 'khaman dhokla'],
    category: 'snack',
    region: 'west',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['besan', 150],
      ['curd', 60],
      ['sugar', 10],
      ['oil', 10],
    ],
    fat: ['oil'],
    cooked: 450,
    pieces: 12,
    piece: ['piece', 'pieces'],
    units: PIECES,
    amount: 3,
  },
  {
    id: 'veg-sandwich',
    name: 'Veg sandwich',
    aliases: ['sandwich', 'bread sandwich'],
    category: 'snack',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['bread', 50],
      ['butter', 5],
      ['cucumber', 20],
      ['tomato', 20],
      ['potato', 30],
    ],
    fat: ['butter'],
    cooked: 125,
    pieces: 1,
    piece: ['sandwich', 'sandwiches'],
    units: PIECES,
  },
  {
    id: 'pav-bhaji',
    name: 'Pav bhaji (bhaji)',
    aliases: ['bhaji', 'pav bhaji'],
    category: 'snack',
    region: 'west',
    meals: ['lunch', 'snacks', 'dinner'],
    lines: [
      ['potato', 300],
      ['cauliflower', 100],
      ['peas', 100],
      ['capsicum', 80],
      ['tomato', 200],
      ['onion', 100],
      ['butter', 50],
    ],
    fat: ['butter'],
    cooked: 900,
    servings: 5,
    units: CURRY,
  },

  // Snacks ----------------------------------------------------------------------------------
  {
    id: 'samosa',
    name: 'Samosa',
    aliases: ['samose', 'singara'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['maida', 30],
      ['potato', 55],
      ['peas', 5],
      ['oil', 15],
    ],
    fat: ['oil'],
    cooked: 90,
    pieces: 1,
    piece: ['samosa', 'samosas'],
    units: PIECES,
  },
  {
    id: 'pakora',
    name: 'Onion pakora',
    aliases: ['pakoda', 'bhajiya', 'bhaji', 'pakore'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['besan', 100],
      ['onion', 200],
      ['oil', 40],
    ],
    fat: ['oil'],
    cooked: 300,
    pieces: 15,
    piece: ['pakora', 'pakoras'],
    units: PIECES,
    amount: 4,
  },
  {
    id: 'aloo-tikki',
    name: 'Aloo tikki',
    aliases: ['tikki', 'potato patty'],
    category: 'snack',
    meals: ['snacks'],
    lines: [
      ['potato', 400],
      ['besan', 20],
      ['peas', 30],
      ['oil', 30],
    ],
    fat: ['oil'],
    cooked: 420,
    pieces: 8,
    piece: ['tikki', 'tikkis'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'chana-chaat',
    name: 'Chana chaat',
    aliases: ['chickpea salad', 'chole chaat'],
    category: 'snack',
    meals: ['snacks'],
    lines: [
      ['kabuli-chana', 100],
      ['onion', 30],
      ['tomato', 40],
      ['lemon', 10],
    ],
    cooked: 340,
    servings: 2,
    units: ['katori', 'g'],
    density: 0.75,
  },
  {
    id: 'sprouts-chaat',
    name: 'Sprouts chaat',
    aliases: ['moong sprouts', 'sprouts salad', 'ankurit moong'],
    category: 'snack',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['moong-sprouts', 100],
      ['onion', 20],
      ['tomato', 30],
      ['lemon', 5],
    ],
    cooked: 155,
    servings: 1,
    units: ['katori', 'g'],
    density: 0.6,
  },
  {
    id: 'green-salad',
    name: 'Green salad',
    aliases: ['salad', 'kachumber'],
    category: 'side',
    region: 'all',
    meals: MAINS,
    lines: [
      ['cucumber', 100],
      ['tomato', 50],
      ['onion', 30],
      ['carrot', 30],
      ['lemon', 5],
    ],
    cooked: 215,
    servings: 2,
    units: ['katori', 'g'],
    density: 0.55,
  },

  // Sweets ----------------------------------------------------------------------------------
  {
    id: 'kheer',
    name: 'Rice kheer',
    aliases: ['kheer', 'payasam', 'payesh'],
    category: 'sweet',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['milk', 1000],
      ['rice', 50],
      ['sugar', 80],
      ['almonds', 10],
    ],
    cooked: 750,
    servings: 5,
    units: ['katori', 'g'],
    density: 1.05,
  },
  {
    id: 'sooji-halwa',
    name: 'Sooji halwa',
    aliases: ['sheera', 'rava kesari', 'halwa'],
    category: 'sweet',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['sooji', 100],
      ['ghee', 60],
      ['sugar', 100],
      ['water', 300],
    ],
    fat: ['ghee'],
    cooked: 450,
    servings: 5,
    units: ['katori', 'tbsp', 'g'],
    density: 0.95,
  },
  {
    id: 'gajar-halwa',
    name: 'Gajar halwa',
    aliases: ['carrot halwa', 'gajrela'],
    category: 'sweet',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['carrot', 500],
      ['milk', 500],
      ['sugar', 100],
      ['ghee', 40],
      ['cashews', 15],
    ],
    fat: ['ghee'],
    cooked: 700,
    servings: 7,
    units: ['katori', 'g'],
    density: 0.95,
  },
  {
    id: 'besan-ladoo',
    name: 'Besan ladoo',
    aliases: ['laddu', 'ladoo', 'besan laddu'],
    category: 'sweet',
    meals: ['snacks'],
    lines: [
      ['besan', 200],
      ['ghee', 100],
      ['sugar', 150],
    ],
    cooked: 440,
    pieces: 16,
    piece: ['ladoo', 'ladoos'],
    units: PIECES,
  },
  {
    id: 'rasgulla',
    name: 'Rasgulla',
    aliases: ['rosogolla', 'rasagola'],
    category: 'sweet',
    region: 'east',
    meals: ['snacks'],
    lines: [
      ['paneer', 200],
      ['sugar', 120],
      ['water', 80],
    ],
    cooked: 400,
    pieces: 10,
    piece: ['rasgulla', 'rasgullas'],
    units: PIECES,
  },

  // Most ordered and street food (Swiggy 2025: biryani, burgers, pizza, dosa, idli, rolls,
  // samosa, momos, cakes, gulab jamun, kaju barfi) ------------------------------------------
  {
    id: 'gulab-jamun',
    name: 'Gulab jamun',
    aliases: ['gulabjamun', 'kala jamun'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks', 'dinner'],
    lines: [
      ['milk-powder', 40],
      ['maida', 8],
      ['ghee', 8],
      ['sugar', 40],
      ['water', 30],
    ],
    fat: ['ghee'],
    cooked: 120,
    pieces: 3,
    piece: ['gulab jamun', 'gulab jamuns'],
    units: PIECES,
  },
  {
    id: 'jalebi',
    name: 'Jalebi',
    aliases: ['jilapi', 'imarti'],
    category: 'sweet',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['maida', 50],
      ['curd', 10],
      ['ghee', 15],
      ['sugar', 60],
      ['water', 25],
    ],
    fat: ['ghee'],
    cooked: 140,
    pieces: 7,
    piece: ['jalebi', 'jalebis'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'kaju-katli',
    name: 'Kaju katli',
    aliases: ['kaju barfi', 'kaju burfi', 'cashew barfi'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['cashews', 100],
      ['sugar', 50],
      ['ghee', 3],
    ],
    cooked: 150,
    pieces: 12,
    piece: ['piece', 'pieces'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'motichoor-ladoo',
    name: 'Motichoor ladoo',
    aliases: ['boondi ladoo', 'laddu'],
    category: 'sweet',
    region: 'north',
    meals: ['snacks'],
    lines: [
      ['besan', 100],
      ['ghee', 45],
      ['sugar', 120],
      ['water', 20],
    ],
    fat: ['ghee'],
    cooked: 270,
    pieces: 9,
    piece: ['ladoo', 'ladoos'],
    units: PIECES,
  },
  {
    id: 'modak',
    name: 'Ukadiche modak',
    aliases: ['modak', 'kozhukattai'],
    category: 'sweet',
    region: 'west',
    meals: ['snacks'],
    lines: [
      ['rice-flour', 100],
      ['coconut', 100],
      ['jaggery', 70],
      ['ghee', 10],
      ['water', 120],
    ],
    fat: ['ghee'],
    cooked: 380,
    pieces: 8,
    piece: ['modak', 'modaks'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'veg-momos',
    name: 'Veg momos',
    aliases: ['momo', 'dumplings', 'steamed momos'],
    category: 'snack',
    region: 'all',
    meals: ['snacks', 'dinner'],
    lines: [
      ['maida', 100],
      ['cabbage', 100],
      ['carrot', 40],
      ['onion', 30],
      ['oil', 8],
      ['soy-sauce', 5],
    ],
    fat: ['oil'],
    cooked: 330,
    pieces: 12,
    piece: ['momo', 'momos'],
    units: PIECES,
    amount: 6,
  },
  {
    id: 'chicken-momos',
    name: 'Chicken momos',
    aliases: ['momo', 'chicken dumplings'],
    category: 'chicken',
    region: 'all',
    meals: ['snacks', 'dinner'],
    lines: [
      ['maida', 100],
      ['chicken', 150],
      ['onion', 40],
      ['oil', 8],
      ['soy-sauce', 5],
    ],
    fat: ['oil'],
    cooked: 330,
    pieces: 12,
    piece: ['momo', 'momos'],
    units: PIECES,
    amount: 6,
  },
  {
    id: 'chicken-roll',
    name: 'Chicken kathi roll',
    aliases: ['chicken roll', 'kathi roll', 'chicken frankie', 'chicken wrap'],
    category: 'chicken',
    region: 'all',
    meals: ['snacks', 'lunch', 'dinner'],
    lines: [
      ['maida', 60],
      ['chicken', 80],
      ['onion', 30],
      ['oil', 12],
      ['ketchup', 10],
    ],
    fat: ['oil'],
    cooked: 220,
    pieces: 1,
    piece: ['roll', 'rolls'],
    units: PIECES,
  },
  {
    id: 'egg-roll',
    name: 'Egg roll',
    aliases: ['anda roll', 'egg kathi roll', 'egg frankie'],
    category: 'egg',
    region: 'east',
    meals: ['snacks', 'dinner'],
    lines: [
      ['maida', 60],
      ['egg', 50],
      ['onion', 30],
      ['oil', 12],
      ['ketchup', 10],
    ],
    fat: ['oil'],
    cooked: 180,
    pieces: 1,
    piece: ['roll', 'rolls'],
    units: PIECES,
  },
  {
    id: 'paneer-roll',
    name: 'Paneer roll',
    aliases: ['paneer kathi roll', 'paneer frankie', 'paneer wrap'],
    category: 'paneer',
    region: 'all',
    meals: ['snacks', 'lunch', 'dinner'],
    lines: [
      ['maida', 60],
      ['paneer', 70],
      ['onion', 30],
      ['capsicum', 20],
      ['oil', 12],
    ],
    fat: ['oil'],
    cooked: 210,
    pieces: 1,
    piece: ['roll', 'rolls'],
    units: PIECES,
  },
  {
    id: 'chicken-shawarma',
    name: 'Chicken shawarma',
    aliases: ['shawarma', 'shawarma roll'],
    category: 'chicken',
    region: 'all',
    meals: ['snacks', 'dinner'],
    lines: [
      ['maida', 60],
      ['chicken', 100],
      ['mayonnaise', 20],
      ['cabbage', 20],
      ['onion', 15],
      ['oil', 6],
    ],
    fat: ['oil'],
    cooked: 230,
    pieces: 1,
    piece: ['roll', 'rolls'],
    units: PIECES,
  },
  {
    id: 'veg-burger',
    name: 'Veg burger (aloo tikki)',
    aliases: ['burger', 'aloo tikki burger', 'mcaloo'],
    category: 'snack',
    region: 'all',
    meals: ['snacks', 'lunch', 'dinner'],
    lines: [
      ['bread', 60],
      ['potato', 70],
      ['peas', 10],
      ['oil', 10],
      ['mayonnaise', 12],
      ['tomato', 15],
      ['onion', 10],
    ],
    fat: ['oil'],
    cooked: 175,
    pieces: 1,
    piece: ['burger', 'burgers'],
    units: PIECES,
  },
  {
    id: 'chicken-burger',
    name: 'Chicken burger',
    aliases: ['burger', 'crispy chicken burger', 'mcchicken'],
    category: 'chicken',
    region: 'all',
    meals: ['snacks', 'lunch', 'dinner'],
    lines: [
      ['bread', 60],
      ['chicken', 75],
      ['maida', 8],
      ['oil', 6],
      ['mayonnaise', 10],
      ['cabbage', 15],
      ['tomato', 10],
    ],
    fat: ['oil'],
    cooked: 175,
    pieces: 1,
    piece: ['burger', 'burgers'],
    units: PIECES,
  },
  {
    id: 'vada-pav',
    name: 'Vada pav',
    aliases: ['wada pav', 'batata vada'],
    category: 'snack',
    region: 'west',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['bread', 40],
      ['potato', 70],
      ['besan', 15],
      ['oil', 12],
    ],
    fat: ['oil'],
    cooked: 140,
    pieces: 1,
    piece: ['vada pav', 'vada pavs'],
    units: PIECES,
  },
  {
    id: 'pani-puri',
    name: 'Pani puri',
    aliases: ['golgappa', 'gol gappe', 'puchka', 'phuchka'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['sooji', 30],
      ['maida', 6],
      ['oil', 6],
      ['potato', 40],
      ['kabuli-chana', 10],
      ['tamarind', 8],
      ['water', 150],
    ],
    fat: ['oil'],
    cooked: 250,
    pieces: 1,
    piece: ['plate of 6', 'plates of 6'],
    units: PIECES,
  },
  {
    id: 'bhel-puri',
    name: 'Bhel puri',
    aliases: ['bhel', 'jhal muri'],
    category: 'snack',
    region: 'west',
    meals: ['snacks'],
    lines: [
      ['puffed-rice', 40],
      ['onion', 25],
      ['tomato', 20],
      ['potato', 20],
      ['tamarind', 8],
      ['peanuts', 8],
      ['besan', 10],
      ['oil', 6],
    ],
    fat: ['oil'],
    cooked: 136,
    servings: 1,
    units: ['serving', 'katori', 'g'],
    density: 0.35,
  },
  {
    id: 'kachori',
    name: 'Khasta kachori',
    aliases: ['kachori', 'dal kachori', 'pyaaz kachori'],
    category: 'snack',
    region: 'north',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['maida', 35],
      ['urad-dal', 12],
      ['oil', 14],
    ],
    fat: ['oil'],
    cooked: 65,
    pieces: 1,
    piece: ['kachori', 'kachoris'],
    units: PIECES,
  },
  {
    id: 'dahi-vada',
    name: 'Dahi vada',
    aliases: ['dahi bhalla', 'thayir vadai'],
    category: 'snack',
    region: 'north',
    meals: ['snacks', 'lunch'],
    lines: [
      ['urad-dal', 100],
      ['oil', 30],
      ['curd', 300],
      ['sugar', 10],
    ],
    fat: ['oil'],
    cooked: 600,
    pieces: 10,
    piece: ['vada with curd', 'vadas with curd'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'misal-pav',
    name: 'Misal (without pav)',
    aliases: ['misal', 'misal pav', 'usal'],
    category: 'snack',
    region: 'west',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['moong-sprouts', 200],
      ['onion', 60],
      ['tomato', 60],
      ['oil', 25],
      ['peanuts', 10],
      ['besan', 30],
      ['water', 200],
    ],
    fat: ['oil'],
    cooked: 520,
    servings: 2,
    units: ['serving', 'katori', 'g'],
    density: 0.9,
  },
  {
    id: 'sabudana-vada',
    name: 'Sabudana vada',
    aliases: ['sago vada', 'vrat vada'],
    category: 'snack',
    region: 'west',
    meals: ['snacks'],
    lines: [
      ['sabudana', 80],
      ['potato', 80],
      ['peanuts', 20],
      ['oil', 20],
    ],
    fat: ['oil'],
    cooked: 200,
    pieces: 6,
    piece: ['vada', 'vadas'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'veg-hakka-noodles',
    name: 'Veg hakka noodles',
    aliases: ['noodles', 'chowmein', 'chow mein', 'hakka noodles'],
    category: 'snack',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['vermicelli', 100],
      ['cabbage', 60],
      ['carrot', 40],
      ['capsicum', 40],
      ['spring-onion', 20],
      ['oil', 20],
      ['soy-sauce', 10],
    ],
    fat: ['oil'],
    cooked: 450,
    servings: 2,
    units: ['serving', 'katori', 'g'],
    density: 0.6,
  },
  {
    id: 'chicken-noodles',
    name: 'Chicken hakka noodles',
    aliases: ['chicken chowmein', 'chicken noodles'],
    category: 'chicken',
    region: 'all',
    meals: ['lunch', 'dinner'],
    lines: [
      ['vermicelli', 100],
      ['chicken', 100],
      ['cabbage', 50],
      ['capsicum', 30],
      ['oil', 20],
      ['soy-sauce', 10],
    ],
    fat: ['oil'],
    cooked: 480,
    servings: 2,
    units: ['serving', 'katori', 'g'],
    density: 0.6,
  },
  {
    id: 'veg-fried-rice',
    name: 'Veg fried rice',
    aliases: ['fried rice', 'chinese fried rice'],
    category: 'rice',
    region: 'all',
    meals: ['lunch', 'dinner'],
    lines: [
      ['rice', 150],
      ['carrot', 40],
      ['beans', 30],
      ['capsicum', 30],
      ['spring-onion', 20],
      ['oil', 20],
      ['soy-sauce', 10],
    ],
    fat: ['oil'],
    cooked: 560,
    servings: 3,
    units: RICE,
    density: 0.66,
  },
  {
    id: 'egg-fried-rice',
    name: 'Egg fried rice',
    aliases: ['fried rice egg'],
    category: 'egg',
    region: 'all',
    meals: ['lunch', 'dinner'],
    lines: [
      ['rice', 150],
      ['egg', 100],
      ['spring-onion', 20],
      ['oil', 20],
      ['soy-sauce', 10],
    ],
    fat: ['oil'],
    cooked: 560,
    servings: 3,
    units: RICE,
    density: 0.66,
  },
  {
    id: 'chicken-fried-rice',
    name: 'Chicken fried rice',
    aliases: ['fried rice chicken'],
    category: 'chicken',
    region: 'all',
    meals: ['lunch', 'dinner'],
    lines: [
      ['rice', 150],
      ['chicken', 120],
      ['carrot', 30],
      ['spring-onion', 20],
      ['oil', 20],
      ['soy-sauce', 10],
    ],
    fat: ['oil'],
    cooked: 580,
    servings: 3,
    units: RICE,
    density: 0.66,
  },
  {
    id: 'veg-manchurian',
    name: 'Veg manchurian (gravy)',
    aliases: ['manchurian', 'gobi manchurian'],
    category: 'snack',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['cabbage', 150],
      ['carrot', 50],
      ['maida', 30],
      ['cornflour', 25],
      ['oil', 30],
      ['soy-sauce', 10],
      ['ketchup', 15],
      ['water', 150],
    ],
    fat: ['oil'],
    cooked: 420,
    servings: 3,
    units: CURRY,
  },
  {
    id: 'chilli-paneer',
    name: 'Chilli paneer (dry)',
    aliases: ['chilli paneer', 'paneer chilli', 'chilly paneer'],
    category: 'paneer',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['paneer', 200],
      ['capsicum', 80],
      ['onion', 60],
      ['cornflour', 20],
      ['oil', 25],
      ['soy-sauce', 10],
      ['ketchup', 10],
    ],
    fat: ['oil'],
    cooked: 380,
    servings: 3,
    units: DRY,
    density: 0.7,
  },
  {
    id: 'chilli-chicken',
    name: 'Chilli chicken',
    aliases: ['chicken chilli', 'chilly chicken'],
    category: 'chicken',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['chicken', 400],
      ['capsicum', 80],
      ['onion', 60],
      ['cornflour', 25],
      ['egg', 25],
      ['oil', 35],
      ['soy-sauce', 15],
    ],
    fat: ['oil'],
    cooked: 500,
    servings: 4,
    units: DRY,
    density: 0.7,
  },
  {
    id: 'chicken-65',
    name: 'Chicken 65',
    aliases: ['chicken sixty five'],
    category: 'chicken',
    region: 'south',
    meals: ['snacks', 'dinner'],
    lines: [
      ['chicken', 400],
      ['curd', 40],
      ['cornflour', 20],
      ['rice-flour', 15],
      ['oil', 20],
    ],
    fat: ['oil'],
    cooked: 380,
    servings: 4,
    units: ['serving', 'g'],
  },
  {
    id: 'red-sauce-pasta',
    name: 'Red sauce pasta',
    aliases: ['pasta', 'arrabbiata', 'tomato pasta'],
    category: 'snack',
    region: 'all',
    meals: ['lunch', 'dinner'],
    lines: [
      ['vermicelli', 100],
      ['tomato', 200],
      ['onion', 40],
      ['capsicum', 30],
      ['oil', 15],
      ['parmesan', 10],
    ],
    fat: ['oil'],
    cooked: 480,
    servings: 2,
    units: ['serving', 'katori', 'g'],
    density: 0.75,
  },
  {
    id: 'white-sauce-pasta',
    name: 'White sauce pasta',
    aliases: ['pasta alfredo', 'white pasta'],
    category: 'snack',
    region: 'all',
    meals: ['lunch', 'dinner'],
    lines: [
      ['vermicelli', 100],
      ['milk', 250],
      ['butter', 20],
      ['maida', 15],
      ['cheese', 25],
      ['corn', 40],
    ],
    fat: ['butter'],
    cooked: 560,
    servings: 2,
    units: ['serving', 'katori', 'g'],
    density: 0.8,
  },
  {
    id: 'garlic-bread',
    name: 'Garlic bread',
    aliases: ['garlic breadsticks', 'garlic toast'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['bread', 100],
      ['butter', 12],
      ['garlic', 6],
    ],
    fat: ['butter'],
    cooked: 118,
    pieces: 4,
    piece: ['piece', 'pieces'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'luchi',
    name: 'Luchi',
    aliases: ['loochi'],
    category: 'bread',
    region: 'east',
    meals: ['breakfast', 'dinner'],
    lines: [
      ['maida', 20],
      ['oil', 5],
    ],
    fat: ['oil'],
    cooked: { moisture: 0.2 },
    pieces: 1,
    piece: ['luchi', 'luchis'],
    units: PIECES,
    amount: 4,
  },
  {
    id: 'kerala-parotta',
    name: 'Kerala parotta',
    aliases: ['malabar parotta', 'porotta', 'parotta'],
    category: 'bread',
    region: 'south',
    meals: ['lunch', 'dinner'],
    lines: [
      ['maida', 60],
      ['oil', 10],
    ],
    fat: ['oil'],
    cooked: { moisture: 0.3 },
    pieces: 1,
    piece: ['parotta', 'parottas'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'appam',
    name: 'Appam',
    aliases: ['palappam', 'hoppers'],
    category: 'south',
    region: 'south',
    meals: ['breakfast', 'dinner'],
    lines: [
      ['rice', 200],
      ['coconut', 50],
      ['sugar', 10],
    ],
    cooked: { moisture: 0.55 },
    pieces: 8,
    piece: ['appam', 'appams'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'bisi-bele-bath',
    name: 'Bisi bele bath',
    aliases: ['bisibelebath', 'sambar rice'],
    category: 'south',
    region: 'south',
    meals: ['lunch', 'dinner'],
    lines: [
      ['rice', 100],
      ['toor-dal', 60],
      ['carrot', 40],
      ['beans', 40],
      ['tamarind', 10],
      ['ghee', 15],
      ['peanuts', 10],
    ],
    fat: ['ghee'],
    cooked: 750,
    servings: 3,
    units: CURRY,
  },
  {
    id: 'cold-coffee',
    name: 'Cold coffee',
    aliases: ['iced coffee', 'coffee shake'],
    category: 'drink',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['milk', 200],
      ['sugar', 15],
      ['coffee-powder', 3],
    ],
    cooked: 220,
    servings: 1,
    units: ['glass', 'g'],
    density: 1.03,
  },
  {
    id: 'mango-lassi',
    name: 'Mango lassi',
    aliases: ['aam lassi'],
    category: 'drink',
    region: 'north',
    meals: ['snacks', 'lunch'],
    lines: [
      ['curd', 150],
      ['mango', 80],
      ['sugar', 15],
      ['milk', 30],
    ],
    cooked: 275,
    servings: 1,
    units: ['glass', 'g'],
    density: 1.05,
  },
  {
    id: 'masala-chai',
    name: 'Adrak chai',
    aliases: ['ginger tea', 'masala tea', 'adrak wali chai'],
    category: 'drink',
    region: 'all',
    meals: ALL_MEALS,
    lines: [
      ['milk', 60],
      ['tea', 90],
      ['sugar', 6],
      ['ginger', 2],
    ],
    cooked: 140,
    servings: 1,
    units: ['cup', 'g'],
    density: 1.02,
  },
  {
    id: 'protein-shake',
    name: 'Whey shake with milk',
    aliases: ['protein shake', 'whey with milk'],
    category: 'drink',
    region: 'all',
    meals: ALL_MEALS,
    lines: [
      ['whey', 30],
      ['milk', 250],
    ],
    cooked: 280,
    servings: 1,
    units: ['serving', 'glass', 'g'],
    density: 1.04,
  },
  {
    id: 'peanut-butter-toast',
    name: 'Peanut butter toast',
    aliases: ['pb toast', 'peanut butter bread'],
    category: 'breakfast',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['bread', 50],
      ['peanut-butter', 16],
    ],
    cooked: 66,
    pieces: 2,
    piece: ['slice', 'slices'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'cornflakes-milk',
    name: 'Cornflakes with milk',
    aliases: ['cornflakes', 'cereal with milk'],
    category: 'breakfast',
    region: 'all',
    meals: ['breakfast'],
    lines: [
      ['cornflakes', 30],
      ['milk', 200],
      ['sugar', 5],
    ],
    cooked: 235,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'tofu-bhurji',
    name: 'Tofu bhurji',
    aliases: ['tofu scramble'],
    category: 'breakfast',
    region: 'all',
    meals: ['breakfast', 'dinner'],
    lines: [
      ['tofu', 200],
      ['onion', 40],
      ['tomato', 40],
      ['oil', 10],
    ],
    fat: ['oil'],
    cooked: 270,
    servings: 2,
    units: DRY,
    density: 0.7,
  },

  // Gym meals
  {
    id: 'roasted-makhana',
    name: 'Makhana, roasted',
    aliases: ['makhana', 'fox nuts', 'phool makhana', 'lotus seeds'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['lotus-seeds', 30],
      ['ghee', 3],
    ],
    fat: ['ghee'],
    cooked: 33,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'egg-white-omelette',
    name: 'Egg white omelette',
    aliases: ['egg white omelet', 'white omelette'],
    category: 'egg',
    region: 'all',
    meals: ['breakfast', 'dinner'],
    lines: [
      ['egg-white', 132],
      ['onion', 15],
      ['tomato', 15],
      ['oil', 3],
    ],
    fat: ['oil'],
    cooked: 150,
    pieces: 1,
    piece: ['omelette (4 whites)', 'omelettes (4 whites each)'],
    units: PIECES,
  },
  {
    id: 'soya-chunks-boiled',
    name: 'Soya chunks, boiled',
    aliases: ['boiled soya', 'soya chunks', 'nutrela boiled'],
    category: 'basic',
    region: 'all',
    meals: MAINS,
    lines: [['soya-chunks', 50]],
    cooked: 150,
    servings: 1,
    units: ['katori', 'serving', 'g'],
    density: 0.5,
  },
  {
    id: 'sattu-drink',
    name: 'Sattu drink',
    aliases: ['sattu sharbat', 'sattu', 'sattu shake'],
    category: 'drink',
    region: 'east',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['besan', 40],
      ['water', 200],
      ['lemon', 5],
    ],
    cooked: 245,
    servings: 1,
    units: ['serving', 'glass', 'g'],
    density: 1.05,
  },
  {
    id: 'overnight-oats',
    name: 'Overnight oats',
    aliases: ['overnight oats', 'oats jar'],
    category: 'breakfast',
    region: 'all',
    meals: ['breakfast'],
    lines: [
      ['oats', 40],
      ['milk', 150],
      ['banana', 50],
      ['peanut-butter', 8],
    ],
    cooked: 248,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'peanut-chikki',
    name: 'Peanut chikki',
    aliases: ['chikki', 'gajak'],
    category: 'sweet',
    region: 'west',
    meals: ['snacks'],
    lines: [
      ['peanuts', 60],
      ['jaggery', 40],
    ],
    cooked: 100,
    pieces: 5,
    piece: ['piece', 'pieces'],
    units: PIECES,
  },
  {
    id: 'chicken-salad',
    name: 'Chicken salad bowl',
    aliases: ['chicken salad', 'protein bowl'],
    category: 'chicken',
    region: 'all',
    meals: MAINS,
    lines: [
      ['chicken-breast-cooked', 120],
      ['cucumber', 60],
      ['tomato', 50],
      ['onion', 20],
      ['oil', 5],
      ['lemon', 5],
    ],
    fat: ['oil'],
    cooked: 260,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'black-coffee',
    name: 'Black coffee',
    aliases: ['americano', 'pre workout coffee'],
    category: 'drink',
    region: 'all',
    meals: ALL_MEALS,
    lines: [['coffee', 200]],
    cooked: 200,
    servings: 1,
    units: ['cup', 'g'],
  },
  {
    id: 'green-tea',
    name: 'Green tea',
    aliases: ['green tea'],
    category: 'drink',
    region: 'all',
    meals: ALL_MEALS,
    lines: [['tea', 200]],
    cooked: 200,
    servings: 1,
    units: ['cup', 'g'],
  },
  {
    id: 'banana-pb-shake',
    name: 'Banana peanut butter shake',
    aliases: ['gainer shake', 'mass gainer shake', 'banana shake'],
    category: 'drink',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['milk', 250],
      ['banana', 120],
      ['peanut-butter', 16],
      ['oats', 20],
    ],
    cooked: 400,
    servings: 1,
    units: ['serving', 'glass', 'g'],
    density: 1.05,
  },

  // Cafe, dessert and global food eaten in Indian cities (October 2026) ----------------------
  {
    id: 'tiramisu',
    name: 'Tiramisu',
    aliases: ['tiramisu'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['ladyfingers', 60],
      ['cream-cheese', 60],
      ['cream', 60],
      ['sugar', 25],
      ['egg', 25],
      ['espresso', 60],
      ['dark-chocolate', 3],
    ],
    cooked: 290,
    pieces: 3,
    piece: ['slice', 'slices'],
    units: PIECES,
  },
  {
    id: 'tres-leches',
    name: 'Tres leches cake',
    aliases: ['tres leches', 'three milk cake', 'milk cake'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['sponge-cake', 100],
      ['condensed-milk', 40],
      ['evaporated-milk', 40],
      ['milk', 30],
      ['cream', 30],
    ],
    cooked: 240,
    pieces: 3,
    piece: ['slice', 'slices'],
    units: PIECES,
  },
  {
    id: 'chocolate-truffle-cake',
    name: 'Chocolate truffle cake',
    aliases: ['chocolate cake', 'truffle cake', 'death by chocolate'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['sponge-cake', 240],
      ['dark-chocolate', 120],
      ['cream', 120],
      ['sugar', 20],
    ],
    cooked: 500,
    pieces: 5,
    piece: ['slice', 'slices'],
    units: PIECES,
  },
  {
    id: 'red-velvet-cake',
    name: 'Red velvet cake',
    aliases: ['red velvet', 'red velvet pastry'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['sponge-cake', 70],
      ['cream-cheese', 20],
      ['butter', 8],
      ['sugar', 15],
    ],
    cooked: 113,
    pieces: 1,
    piece: ['slice', 'slices'],
    units: PIECES,
  },
  {
    id: 'cupcake',
    name: 'Cupcake with frosting',
    aliases: ['cupcake', 'cup cake'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['sponge-cake', 45],
      ['butter', 6],
      ['sugar', 12],
    ],
    cooked: 63,
    pieces: 1,
    piece: ['cupcake', 'cupcakes'],
    units: PIECES,
  },
  {
    id: 'sizzling-brownie',
    name: 'Sizzling brownie with ice cream',
    aliases: ['sizzling brownie', 'brownie with ice cream', 'brownie sundae'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['brownie', 80],
      ['ice-cream', 60],
      ['milk-chocolate', 15],
    ],
    cooked: 155,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'chocolate-mousse',
    name: 'Chocolate mousse',
    aliases: ['mousse'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['cream', 40],
      ['milk', 40],
      ['dark-chocolate', 25],
      ['sugar', 8],
      ['egg', 20],
    ],
    cooked: 133,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'panna-cotta',
    name: 'Panna cotta',
    aliases: ['pannacotta'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['cream', 60],
      ['milk', 60],
      ['sugar', 15],
    ],
    cooked: 135,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'baklava',
    name: 'Baklava',
    aliases: ['baklawa'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['phyllo', 30],
      ['cashews', 30],
      ['butter', 15],
      ['honey', 25],
    ],
    cooked: 100,
    pieces: 4,
    piece: ['piece', 'pieces'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'churros',
    name: 'Churros',
    aliases: ['churro'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['maida', 40],
      ['water', 50],
      ['oil', 12],
      ['sugar', 8],
    ],
    fat: ['oil'],
    cooked: 80,
    pieces: 4,
    piece: ['churro', 'churros'],
    units: PIECES,
    amount: 4,
  },
  {
    id: 'blueberry-muffin',
    name: 'Blueberry muffin',
    aliases: ['muffin', 'chocolate muffin'],
    category: 'sweet',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['maida', 40],
      ['sugar', 20],
      ['oil', 12],
      ['egg', 15],
      ['milk', 20],
      ['blueberries', 15],
    ],
    cooked: 110,
    pieces: 1,
    piece: ['muffin', 'muffins'],
    units: PIECES,
  },
  {
    id: 'belgian-waffle',
    name: 'Belgian waffle with chocolate',
    aliases: ['waffle', 'chocolate waffle', 'belgian waffle'],
    category: 'sweet',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['waffle', 100],
      ['milk-chocolate', 20],
    ],
    cooked: 120,
    pieces: 1,
    piece: ['waffle', 'waffles'],
    units: PIECES,
  },
  {
    id: 'pancakes-maple',
    name: 'Pancakes with maple syrup',
    aliases: ['pancakes', 'pancake stack', 'hotcakes'],
    category: 'breakfast',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['pancake', 120],
      ['maple-syrup', 30],
      ['butter', 5],
    ],
    cooked: 155,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'french-toast',
    name: 'French toast',
    aliases: ['bread toast egg', 'eggy bread'],
    category: 'breakfast',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['bread', 60],
      ['egg', 50],
      ['milk', 30],
      ['butter', 8],
      ['sugar', 5],
    ],
    fat: ['butter'],
    cooked: 140,
    pieces: 2,
    piece: ['slice', 'slices'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'chocolate-crepe',
    name: 'Chocolate crepe',
    aliases: ['crepe', 'nutella crepe'],
    category: 'sweet',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['maida', 25],
      ['milk', 60],
      ['egg', 15],
      ['butter', 5],
      ['milk-chocolate', 20],
    ],
    cooked: 110,
    pieces: 1,
    piece: ['crepe', 'crepes'],
    units: PIECES,
  },
  {
    id: 'chia-pudding',
    name: 'Chia pudding',
    aliases: ['chia seed pudding'],
    category: 'breakfast',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['chia', 20],
      ['milk', 150],
      ['honey', 10],
    ],
    cooked: 180,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'cappuccino',
    name: 'Cappuccino',
    aliases: ['cappucino', 'capuccino'],
    category: 'drink',
    region: 'all',
    meals: ALL_MEALS,
    lines: [
      ['espresso', 30],
      ['milk', 120],
    ],
    cooked: 150,
    pieces: 1,
    piece: ['cup', 'cups'],
    units: PIECES,
  },
  {
    id: 'cafe-latte',
    name: 'Cafe latte',
    aliases: ['latte', 'coffee latte'],
    category: 'drink',
    region: 'all',
    meals: ALL_MEALS,
    lines: [
      ['espresso', 30],
      ['milk', 200],
    ],
    cooked: 230,
    pieces: 1,
    piece: ['cup', 'cups'],
    units: PIECES,
  },
  {
    id: 'cafe-mocha',
    name: 'Cafe mocha',
    aliases: ['mocha', 'mocha latte'],
    category: 'drink',
    region: 'all',
    meals: ALL_MEALS,
    lines: [
      ['espresso', 30],
      ['milk', 180],
      ['milk-chocolate', 20],
      ['whipped-cream', 10],
    ],
    cooked: 240,
    pieces: 1,
    piece: ['cup', 'cups'],
    units: PIECES,
  },
  {
    id: 'hot-chocolate',
    name: 'Hot chocolate',
    aliases: ['hot cocoa', 'drinking chocolate'],
    category: 'drink',
    region: 'all',
    meals: ALL_MEALS,
    lines: [
      ['milk', 200],
      ['milk-chocolate', 25],
      ['sugar', 5],
    ],
    cooked: 230,
    pieces: 1,
    piece: ['cup', 'cups'],
    units: PIECES,
  },
  {
    id: 'bubble-tea',
    name: 'Bubble tea',
    aliases: ['boba', 'boba tea', 'milk tea'],
    category: 'drink',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['tea', 300],
      ['milk', 75],
      ['cream', 15],
      ['sugar', 35],
      ['tapioca-pearls', 35],
    ],
    cooked: 520,
    pieces: 1,
    piece: ['cup (500 ml)', 'cups (500 ml)'],
    units: PIECES,
  },
  {
    id: 'coffee-frappe',
    name: 'Coffee frappe',
    aliases: ['frappe', 'frappuccino', 'mocha frappe', 'caramel frappe'],
    category: 'drink',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['milk', 150],
      ['coffee', 50],
      ['sugar', 20],
      ['ice-cream', 40],
      ['whipped-cream', 15],
    ],
    cooked: 275,
    pieces: 1,
    piece: ['glass (tall)', 'glasses (tall)'],
    units: PIECES,
  },
  {
    id: 'fruit-smoothie',
    name: 'Banana berry smoothie',
    aliases: ['smoothie', 'fruit smoothie'],
    category: 'drink',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['banana', 100],
      ['strawberries', 80],
      ['curd', 100],
      ['honey', 10],
    ],
    cooked: 290,
    pieces: 1,
    piece: ['glass', 'glasses'],
    units: PIECES,
  },
  {
    id: 'smoothie-bowl',
    name: 'Smoothie bowl',
    aliases: ['acai bowl', 'granola bowl'],
    category: 'breakfast',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['banana', 100],
      ['blueberries', 50],
      ['curd', 100],
      ['granola', 30],
      ['chia', 5],
    ],
    cooked: 285,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'avocado-toast',
    name: 'Avocado toast',
    aliases: ['avocado toast', 'avo toast', 'avocado on toast'],
    category: 'breakfast',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['sourdough', 70],
      ['avocado', 70],
      ['olive-oil', 5],
      ['lemon', 3],
    ],
    cooked: 148,
    pieces: 2,
    piece: ['slice', 'slices'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'avocado-toast-egg',
    name: 'Avocado toast with egg',
    aliases: ['avocado egg toast', 'avocado toast poached egg'],
    category: 'breakfast',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['sourdough', 70],
      ['avocado', 70],
      ['olive-oil', 5],
      ['lemon', 3],
      ['egg', 50],
    ],
    cooked: 198,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'bagel-cream-cheese',
    name: 'Bagel with cream cheese',
    aliases: ['bagel', 'cream cheese bagel'],
    category: 'breakfast',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['bagel', 90],
      ['cream-cheese', 30],
    ],
    cooked: 120,
    pieces: 1,
    piece: ['bagel', 'bagels'],
    units: PIECES,
  },
  {
    id: 'club-sandwich',
    name: 'Chicken club sandwich',
    aliases: ['club sandwich', 'chicken sandwich'],
    category: 'chicken',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['bread', 90],
      ['chicken-breast-cooked', 50],
      ['egg', 25],
      ['mayonnaise', 15],
      ['lettuce', 15],
      ['tomato', 20],
      ['cheese', 15],
    ],
    cooked: 230,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'grilled-cheese-sandwich',
    name: 'Grilled cheese sandwich',
    aliases: ['cheese sandwich', 'cheese toast'],
    category: 'snack',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['bread', 60],
      ['cheese', 30],
      ['butter', 8],
    ],
    cooked: 98,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'falafel-wrap',
    name: 'Falafel wrap',
    aliases: ['falafel pita', 'falafel roll'],
    category: 'snack',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['pita', 60],
      ['falafel', 70],
      ['hummus', 20],
      ['lettuce', 15],
      ['tomato', 20],
    ],
    cooked: 185,
    pieces: 1,
    piece: ['wrap', 'wraps'],
    units: PIECES,
  },
  {
    id: 'hummus-pita',
    name: 'Hummus with pita',
    aliases: ['hummus pita', 'hummus platter'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['hummus', 60],
      ['pita', 60],
      ['olive-oil', 5],
    ],
    cooked: 125,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'greek-salad',
    name: 'Greek salad',
    aliases: ['feta salad'],
    category: 'side',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['lettuce', 60],
      ['cucumber', 60],
      ['tomato', 60],
      ['feta', 30],
      ['olives', 15],
      ['olive-oil', 8],
      ['onion', 15],
    ],
    cooked: 248,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'caesar-salad',
    name: 'Chicken caesar salad',
    aliases: ['caesar salad', 'caesar'],
    category: 'chicken',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['lettuce', 100],
      ['chicken-breast-cooked', 80],
      ['parmesan', 10],
      ['bread', 15],
      ['mayonnaise', 15],
      ['olive-oil', 5],
    ],
    cooked: 225,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'veg-sushi',
    name: 'Veg sushi roll',
    aliases: ['sushi', 'maki', 'cucumber roll', 'avocado roll'],
    category: 'snack',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['sushi-rice', 150],
      ['nori', 3],
      ['cucumber', 30],
      ['avocado', 30],
      ['sugar', 5],
      ['vinegar', 10],
    ],
    cooked: 228,
    pieces: 8,
    piece: ['piece', 'pieces'],
    units: PIECES,
    amount: 8,
  },
  {
    id: 'salmon-sushi',
    name: 'Salmon sushi roll',
    aliases: ['salmon sushi', 'salmon roll', 'sushi'],
    category: 'fish',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['sushi-rice', 150],
      ['nori', 3],
      ['salmon', 60],
      ['cucumber', 20],
      ['sugar', 5],
      ['vinegar', 10],
    ],
    cooked: 248,
    pieces: 8,
    piece: ['piece', 'pieces'],
    units: PIECES,
    amount: 8,
  },
  {
    id: 'chicken-burrito',
    name: 'Chicken burrito',
    aliases: ['burrito'],
    category: 'chicken',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['flour-tortilla', 75],
      ['rice', 30],
      ['water', 50],
      ['chicken', 80],
      ['rajma-boiled', 40],
      ['cheese', 15],
      ['sweet-corn', 20],
      ['tomato', 20],
      ['oil', 5],
    ],
    fat: ['oil'],
    cooked: 315,
    pieces: 1,
    piece: ['burrito', 'burritos'],
    units: PIECES,
  },
  {
    id: 'veg-burrito-bowl',
    name: 'Veg burrito bowl',
    aliases: ['burrito bowl', 'mexican bowl', 'rice bowl'],
    category: 'rice',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['rice', 50],
      ['water', 85],
      ['rajma-boiled', 80],
      ['sweet-corn', 40],
      ['tomato', 40],
      ['onion', 20],
      ['cheese', 15],
      ['curd', 30],
      ['lettuce', 20],
    ],
    cooked: 380,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'chicken-tacos',
    name: 'Chicken tacos',
    aliases: ['tacos', 'taco'],
    category: 'chicken',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['flour-tortilla', 60],
      ['chicken', 70],
      ['onion', 15],
      ['tomato', 20],
      ['cheese', 10],
      ['curd', 15],
      ['oil', 4],
    ],
    fat: ['oil'],
    cooked: 175,
    pieces: 2,
    piece: ['taco', 'tacos'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'nachos-cheese',
    name: 'Nachos with cheese',
    aliases: ['nachos', 'loaded nachos'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['flour-tortilla', 60],
      ['oil', 15],
      ['cheese', 40],
      ['tomato', 30],
      ['onion', 10],
    ],
    fat: ['oil'],
    cooked: 155,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'cheese-quesadilla',
    name: 'Cheese quesadilla',
    aliases: ['quesadilla'],
    category: 'snack',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['flour-tortilla', 80],
      ['cheese', 50],
      ['butter', 5],
      ['capsicum', 20],
      ['onion', 10],
    ],
    cooked: 165,
    pieces: 1,
    piece: ['quesadilla', 'quesadillas'],
    units: PIECES,
  },
  {
    id: 'mac-and-cheese',
    name: 'Mac and cheese',
    aliases: ['macaroni cheese', 'mac n cheese'],
    category: 'snack',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['pasta-cooked', 150],
      ['cheese', 40],
      ['milk', 60],
      ['butter', 8],
      ['maida', 5],
    ],
    cooked: 263,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'margherita-pizza',
    name: 'Margherita pizza',
    aliases: ['pizza', 'cheese pizza', 'dominos margherita'],
    category: 'snack',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['maida', 130],
      ['water', 80],
      ['oil', 8],
      ['mozzarella', 90],
      ['tomato', 60],
    ],
    cooked: 330,
    pieces: 6,
    piece: ['slice', 'slices'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'veg-pizza',
    name: 'Veg pizza (farmhouse style)',
    aliases: ['pizza', 'veg pizza', 'farmhouse pizza', 'veggie pizza'],
    category: 'snack',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['maida', 130],
      ['water', 80],
      ['oil', 8],
      ['mozzarella', 90],
      ['tomato', 60],
      ['capsicum', 40],
      ['onion', 30],
      ['mushroom', 30],
      ['sweet-corn', 20],
    ],
    cooked: 430,
    pieces: 6,
    piece: ['slice', 'slices'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'chicken-pizza',
    name: 'Chicken pizza',
    aliases: ['pizza', 'chicken tikka pizza', 'bbq chicken pizza'],
    category: 'chicken',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['maida', 130],
      ['water', 80],
      ['oil', 8],
      ['mozzarella', 90],
      ['tomato', 60],
      ['chicken', 80],
      ['onion', 30],
      ['capsicum', 30],
    ],
    cooked: 410,
    pieces: 6,
    piece: ['slice', 'slices'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'french-fries',
    name: 'French fries',
    aliases: ['fries', 'finger chips', 'peri peri fries', 'chips'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['potato', 150],
      ['oil', 18],
    ],
    fat: ['oil'],
    cooked: 93,
    servings: 1,
    units: ['serving', 'g'],
  },
  {
    id: 'fried-chicken',
    name: 'Fried chicken (crispy, bone-in)',
    aliases: ['fried chicken', 'kfc chicken', 'crispy chicken', 'chicken bucket'],
    category: 'chicken',
    region: 'all',
    meals: ['lunch', 'dinner', 'snacks'],
    lines: [
      ['chicken', 150],
      ['maida', 20],
      ['oil', 20],
    ],
    fat: ['oil'],
    cooked: 140,
    pieces: 2,
    piece: ['piece', 'pieces'],
    units: PIECES,
    amount: 2,
  },

  // Drinks ----------------------------------------------------------------------------------
  {
    id: 'chai',
    name: 'Chai with sugar',
    aliases: ['tea', 'masala chai', 'cutting chai', 'doodh wali chai'],
    category: 'drink',
    region: 'all',
    meals: ALL_MEALS,
    lines: [
      ['milk', 50],
      ['tea', 100],
      ['sugar', 6],
    ],
    cooked: 140,
    servings: 1,
    units: ['cup', 'g'],
    density: 1.02,
  },
  {
    id: 'chai-no-sugar',
    name: 'Chai without sugar',
    aliases: ['tea no sugar', 'chai bina cheeni', 'sugar free chai'],
    category: 'drink',
    region: 'all',
    meals: ALL_MEALS,
    lines: [
      ['milk', 50],
      ['tea', 100],
    ],
    cooked: 135,
    servings: 1,
    units: ['cup', 'g'],
    density: 1.02,
  },
  {
    id: 'filter-coffee',
    name: 'Filter coffee',
    aliases: ['coffee', 'kaapi', 'milk coffee'],
    category: 'drink',
    region: 'south',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['milk', 100],
      ['coffee', 50],
      ['sugar', 8],
    ],
    cooked: 155,
    servings: 1,
    units: ['cup', 'g'],
    density: 1.02,
  },
  {
    id: 'sweet-lassi',
    name: 'Sweet lassi',
    aliases: ['lassi', 'meethi lassi'],
    category: 'drink',
    meals: ['lunch', 'snacks'],
    lines: [
      ['curd', 200],
      ['sugar', 20],
      ['water', 50],
    ],
    cooked: 270,
    servings: 1,
    units: ['glass', 'g'],
    density: 1.04,
  },
  {
    id: 'chaas',
    name: 'Chaas',
    aliases: ['buttermilk', 'mattha', 'majjige', 'moru', 'chhachh'],
    category: 'drink',
    region: 'all',
    meals: ['lunch', 'snacks'],
    lines: [
      ['curd', 100],
      ['water', 150],
      ['cumin', 1],
    ],
    cooked: 250,
    servings: 1,
    units: ['glass', 'g'],
    density: 1.02,
  },
  {
    id: 'nimbu-pani',
    name: 'Nimbu pani',
    aliases: ['lemonade', 'shikanji', 'lemon water'],
    category: 'drink',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['lemon', 15],
      ['sugar', 15],
      ['water', 200],
    ],
    cooked: 230,
    servings: 1,
    units: ['glass', 'g'],
    density: 1.02,
  },
  {
    id: 'banana-shake',
    name: 'Banana shake',
    aliases: ['banana milkshake', 'kela shake'],
    category: 'drink',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['milk', 200],
      ['banana', 100],
      ['sugar', 10],
    ],
    cooked: 310,
    servings: 1,
    units: ['glass', 'g'],
    density: 1.05,
  },

  // Street food, regional dishes, sweets and drinks (October 2026)
  {
    id: 'chole-bhature',
    name: 'Chole bhature',
    aliases: ['chhole bhature', 'chola bhatura', 'chole bhatura'],
    category: 'breakfast',
    region: 'north',
    meals: ['breakfast', 'lunch'],
    lines: [
      ['maida', 60],
      ['curd', 8],
      ['oil', 28],
      ['kabuli-chana', 50],
      ['onion', 40],
      ['tomato', 40],
    ],
    fat: ['oil'],
    cooked: 280,
    pieces: 1,
    piece: ['plate', 'plates'],
    units: PIECES,
  },
  {
    id: 'chole-kulche',
    name: 'Chole kulche',
    aliases: ['kulche chole', 'chole kulcha'],
    category: 'breakfast',
    region: 'north',
    meals: ['breakfast', 'lunch'],
    lines: [
      ['maida', 70],
      ['butter', 8],
      ['kabuli-chana', 50],
      ['onion', 40],
      ['tomato', 40],
      ['oil', 12],
    ],
    fat: ['oil'],
    cooked: 270,
    pieces: 1,
    piece: ['plate', 'plates'],
    units: PIECES,
  },
  {
    id: 'amritsari-kulcha',
    name: 'Amritsari aloo kulcha',
    aliases: ['aloo kulcha', 'amritsari kulcha', 'stuffed kulcha'],
    category: 'bread',
    region: 'north',
    meals: ['breakfast', 'lunch'],
    lines: [
      ['maida', 70],
      ['potato', 60],
      ['oil', 10],
      ['butter', 6],
      ['onion', 15],
      ['curd', 10],
    ],
    fat: ['oil'],
    cooked: 170,
    pieces: 1,
    piece: ['kulcha', 'kulchas'],
    units: PIECES,
  },
  {
    id: 'litti-chokha',
    name: 'Litti chokha',
    aliases: ['litti', 'bihari litti chokha', 'baati chokha'],
    category: 'breakfast',
    region: 'east',
    meals: ['lunch', 'dinner'],
    lines: [
      ['atta', 60],
      ['besan', 25],
      ['ghee', 12],
      ['brinjal', 80],
      ['tomato', 40],
      ['onion', 20],
      ['mustard-oil', 6],
      ['potato', 40],
    ],
    fat: ['ghee', 'mustard-oil'],
    cooked: 330,
    pieces: 1,
    piece: ['plate of 2 litti', 'plates of 2 litti'],
    units: PIECES,
  },
  {
    id: 'dal-baati',
    name: 'Dal baati',
    aliases: ['dal bati', 'dal baati churma', 'rajasthani dal baati'],
    category: 'dal',
    region: 'west',
    meals: ['lunch', 'dinner'],
    lines: [
      ['atta', 90],
      ['ghee', 20],
      ['toor-dal', 25],
      ['moong-dal', 10],
      ['ghee', 6],
      ['onion', 25],
      ['tomato', 25],
    ],
    fat: ['ghee'],
    cooked: 380,
    pieces: 1,
    piece: ['plate of 2 baati with dal', 'plates of 2 baati with dal'],
    units: PIECES,
  },
  {
    id: 'dabeli',
    name: 'Dabeli',
    aliases: ['kutchi dabeli', 'double roti'],
    category: 'snack',
    region: 'west',
    meals: ['snacks'],
    lines: [
      ['bread', 55],
      ['potato', 60],
      ['peanuts', 8],
      ['tamarind', 6],
      ['besan', 6],
      ['oil', 8],
      ['butter', 5],
      ['onion', 10],
    ],
    fat: ['oil'],
    cooked: 150,
    pieces: 1,
    piece: ['dabeli', 'dabelis'],
    units: PIECES,
  },
  {
    id: 'ragda-pattice',
    name: 'Ragda pattice',
    aliases: ['ragda patties', 'ragda pattis', 'white peas patties'],
    category: 'snack',
    region: 'west',
    meals: ['snacks'],
    lines: [
      ['chickpeas-boiled', 120],
      ['potato', 130],
      ['oil', 14],
      ['cornflour', 8],
      ['onion', 25],
      ['tamarind', 10],
      ['tomato', 20],
      ['besan', 8],
    ],
    fat: ['oil'],
    cooked: 330,
    pieces: 1,
    piece: ['plate', 'plates'],
    units: PIECES,
  },
  {
    id: 'sev-puri',
    name: 'Sev puri',
    aliases: ['sev poori', 'sevpuri'],
    category: 'snack',
    region: 'west',
    meals: ['snacks'],
    lines: [
      ['maida', 30],
      ['oil', 12],
      ['potato', 35],
      ['onion', 15],
      ['tomato', 12],
      ['besan', 12],
      ['tamarind', 8],
      ['sugar', 4],
    ],
    fat: ['oil'],
    cooked: 130,
    pieces: 1,
    piece: ['plate of 6', 'plates of 6'],
    units: PIECES,
  },
  {
    id: 'dahi-puri',
    name: 'Dahi puri',
    aliases: ['dahi poori', 'dahi batata puri'],
    category: 'snack',
    region: 'west',
    meals: ['snacks'],
    lines: [
      ['maida', 30],
      ['oil', 12],
      ['potato', 30],
      ['curd', 40],
      ['tamarind', 6],
      ['sugar', 4],
      ['besan', 8],
      ['onion', 10],
    ],
    fat: ['oil'],
    cooked: 150,
    pieces: 1,
    piece: ['plate of 6', 'plates of 6'],
    units: PIECES,
  },
  {
    id: 'papdi-chaat',
    name: 'Papdi chaat',
    aliases: ['papri chaat', 'dahi papdi chaat'],
    category: 'snack',
    region: 'north',
    meals: ['snacks'],
    lines: [
      ['maida', 35],
      ['oil', 10],
      ['chickpeas-boiled', 30],
      ['potato', 30],
      ['curd', 50],
      ['tamarind', 8],
      ['sugar', 4],
      ['besan', 8],
    ],
    fat: ['oil'],
    cooked: 200,
    pieces: 1,
    piece: ['plate', 'plates'],
    units: PIECES,
  },
  {
    id: 'aloo-chaat',
    name: 'Aloo chaat',
    aliases: ['aloo chat', 'fried potato chaat', 'potato chaat'],
    category: 'snack',
    region: 'north',
    meals: ['snacks'],
    lines: [
      ['potato', 150],
      ['oil', 15],
      ['tamarind', 8],
      ['onion', 15],
      ['tomato', 15],
      ['sugar', 3],
    ],
    fat: ['oil'],
    cooked: 170,
    pieces: 1,
    piece: ['plate', 'plates'],
    units: PIECES,
  },
  {
    id: 'tamatar-chaat',
    name: 'Tamatar chaat',
    aliases: ['tamatar chat', 'tomato chaat', 'banarasi tamatar chaat', 'tamatar ki chaat'],
    category: 'snack',
    region: 'north',
    meals: ['snacks'],
    lines: [
      ['tomato', 120],
      ['potato', 60],
      ['ghee', 8],
      ['besan', 8],
      ['tamarind', 5],
      ['onion', 10],
    ],
    fat: ['ghee'],
    cooked: 200,
    pieces: 1,
    piece: ['plate', 'plates'],
    units: PIECES,
  },
  {
    id: 'aloo-tikki-chaat',
    name: 'Aloo tikki chaat',
    aliases: ['tikki chaat', 'tikki chat', 'aloo tikki chat', 'aloo tikki with chole'],
    category: 'snack',
    region: 'north',
    meals: ['snacks'],
    lines: [
      ['potato', 140],
      ['cornflour', 10],
      ['oil', 18],
      ['chickpeas-boiled', 50],
      ['curd', 40],
      ['tamarind', 8],
      ['onion', 15],
      ['sugar', 3],
      ['besan', 6],
    ],
    fat: ['oil'],
    cooked: 330,
    pieces: 1,
    piece: ['plate of 2 tikki', 'plates of 2 tikki'],
    units: PIECES,
  },
  {
    id: 'dahi-puchka',
    name: 'Dahi golgappa',
    aliases: ['dahi puchka', 'dahi gol gappe', 'dahi pani puri', 'curd puchka', 'dahi phuchka'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['maida', 25],
      ['oil', 10],
      ['potato', 25],
      ['chickpeas-boiled', 15],
      ['curd', 60],
      ['tamarind', 6],
      ['sugar', 5],
      ['besan', 6],
    ],
    fat: ['oil'],
    cooked: 190,
    pieces: 1,
    piece: ['plate of 6', 'plates of 6'],
    units: PIECES,
  },
  {
    id: 'raj-kachori',
    name: 'Raj kachori',
    aliases: ['raj kachori chaat', 'raaj kachori', 'royal kachori'],
    category: 'snack',
    region: 'north',
    meals: ['snacks'],
    lines: [
      ['maida', 50],
      ['urad-dal', 10],
      ['oil', 24],
      ['potato', 40],
      ['chickpeas-boiled', 30],
      ['curd', 60],
      ['tamarind', 8],
      ['sugar', 4],
      ['besan', 10],
    ],
    fat: ['oil'],
    cooked: 260,
    pieces: 1,
    piece: ['raj kachori', 'raj kachoris'],
    units: PIECES,
  },
  {
    id: 'kachori-chaat',
    name: 'Kachori chaat',
    aliases: ['kachori chat', 'dahi kachori', 'kachori with chole'],
    category: 'snack',
    region: 'north',
    meals: ['snacks'],
    lines: [
      ['maida', 35],
      ['urad-dal', 12],
      ['oil', 14],
      ['potato', 40],
      ['chickpeas-boiled', 40],
      ['curd', 50],
      ['tamarind', 8],
      ['sugar', 3],
      ['besan', 6],
    ],
    fat: ['oil'],
    cooked: 230,
    pieces: 1,
    piece: ['plate', 'plates'],
    units: PIECES,
  },
  {
    id: 'corn-chaat',
    name: 'Corn chaat',
    aliases: ['masala corn', 'sweet corn chaat', 'American corn chaat', 'corn cup'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['corn', 130],
      ['butter', 5],
      ['onion', 10],
      ['tomato', 10],
      ['lemon', 5],
    ],
    cooked: 150,
    pieces: 1,
    piece: ['cup', 'cups'],
    units: PIECES,
  },
  {
    id: 'fruit-chaat',
    name: 'Fruit chaat',
    aliases: ['fal chaat', 'phal chaat', 'fruit chat', 'mixed fruit chaat'],
    category: 'fruit',
    region: 'north',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['apple', 60],
      ['banana', 60],
      ['papaya', 60],
      ['pomegranate', 30],
      ['orange', 40],
      ['lemon', 3],
    ],
    cooked: 250,
    pieces: 1,
    piece: ['bowl', 'bowls'],
    units: PIECES,
  },
  {
    id: 'palak-patta-chaat',
    name: 'Palak patta chaat',
    aliases: ['palak chaat', 'spinach chaat', 'crispy palak chaat'],
    category: 'snack',
    region: 'north',
    meals: ['snacks'],
    lines: [
      ['spinach', 40],
      ['besan', 30],
      ['rice-flour', 10],
      ['oil', 20],
      ['curd', 40],
      ['tamarind', 8],
      ['sugar', 3],
    ],
    fat: ['oil'],
    cooked: 150,
    pieces: 1,
    piece: ['plate', 'plates'],
    units: PIECES,
  },
  {
    id: 'basket-chaat',
    name: 'Basket chaat',
    aliases: ['tokri chaat', 'tokri chat', 'potato basket chaat', 'aloo tokri'],
    category: 'snack',
    region: 'north',
    meals: ['snacks'],
    lines: [
      ['potato', 120],
      ['cornflour', 8],
      ['oil', 20],
      ['chickpeas-boiled', 40],
      ['curd', 40],
      ['tamarind', 8],
      ['onion', 10],
      ['tomato', 10],
      ['sugar', 3],
    ],
    fat: ['oil'],
    cooked: 220,
    pieces: 1,
    piece: ['basket', 'baskets'],
    units: PIECES,
  },
  {
    id: 'samosa-chaat',
    name: 'Samosa chaat',
    aliases: ['samosa chat', 'samosa chole chaat'],
    category: 'snack',
    region: 'north',
    meals: ['snacks'],
    lines: [
      ['maida', 40],
      ['potato', 60],
      ['peas', 15],
      ['oil', 25],
      ['chickpeas-boiled', 60],
      ['curd', 40],
      ['tamarind', 8],
      ['onion', 15],
      ['sugar', 3],
    ],
    fat: ['oil'],
    cooked: 300,
    pieces: 1,
    piece: ['plate', 'plates'],
    units: PIECES,
  },
  {
    id: 'bun-maska',
    name: 'Bun maska',
    aliases: ['maska bun', 'irani cafe bun maska'],
    category: 'breakfast',
    region: 'west',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['bread', 60],
      ['butter', 14],
      ['sugar', 4],
    ],
    cooked: 78,
    pieces: 1,
    piece: ['bun', 'buns'],
    units: PIECES,
  },
  {
    id: 'maska-pav',
    name: 'Maska pav',
    aliases: ['butter pav', 'pav with butter'],
    category: 'breakfast',
    region: 'west',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['bread', 80],
      ['butter', 15],
    ],
    cooked: 95,
    pieces: 2,
    piece: ['pav', 'pavs'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'keema-pav',
    name: 'Keema pav',
    aliases: ['mutton keema pav', 'kheema pav'],
    category: 'meat',
    region: 'west',
    meals: ['breakfast', 'lunch', 'dinner'],
    lines: [
      ['mutton', 100],
      ['onion', 50],
      ['tomato', 40],
      ['oil', 12],
      ['peas', 20],
      ['bread', 80],
    ],
    fat: ['oil'],
    cooked: 330,
    pieces: 1,
    piece: ['plate (keema with 2 pav)', 'plates (keema with 2 pav)'],
    units: PIECES,
  },
  {
    id: 'egg-bhurji-pav',
    name: 'Egg bhurji pav',
    aliases: ['anda bhurji pav', 'bhurji pav', 'egg pav'],
    category: 'egg',
    region: 'west',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['egg', 100],
      ['onion', 30],
      ['tomato', 25],
      ['oil', 10],
      ['bread', 80],
    ],
    fat: ['oil'],
    cooked: 260,
    pieces: 1,
    piece: ['plate (bhurji with 2 pav)', 'plates (bhurji with 2 pav)'],
    units: PIECES,
  },
  {
    id: 'egg-puff',
    name: 'Egg puff',
    aliases: ['anda puff', 'bakery egg puff'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['puff-pastry', 45],
      ['egg', 40],
      ['onion', 10],
      ['oil', 3],
    ],
    fat: ['oil'],
    cooked: 90,
    pieces: 1,
    piece: ['puff', 'puffs'],
    units: PIECES,
  },
  {
    id: 'veg-puff',
    name: 'Veg puff',
    aliases: ['bakery veg puff', 'aloo puff'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['puff-pastry', 45],
      ['potato', 35],
      ['onion', 10],
      ['peas', 8],
      ['oil', 4],
    ],
    fat: ['oil'],
    cooked: 90,
    pieces: 1,
    piece: ['puff', 'puffs'],
    units: PIECES,
  },
  {
    id: 'bread-pakora',
    name: 'Bread pakora',
    aliases: ['bread pakoda', 'bread bhajia'],
    category: 'snack',
    region: 'north',
    meals: ['snacks'],
    lines: [
      ['bread', 50],
      ['besan', 25],
      ['potato', 40],
      ['oil', 15],
    ],
    fat: ['oil'],
    cooked: 120,
    pieces: 2,
    piece: ['pakora', 'pakoras'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'roasted-corn',
    name: 'Roasted corn on the cob',
    aliases: ['bhutta', 'bhutta roasted', 'corn cob', 'masala corn'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['corn', 130],
      ['butter', 6],
      ['lemon', 5],
    ],
    cooked: 130,
    pieces: 1,
    piece: ['cob', 'cobs'],
    units: PIECES,
  },
  {
    id: 'masala-peanuts',
    name: 'Masala peanuts',
    aliases: ['besan peanuts', 'chatpata peanuts', 'masala moongfali'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['peanuts', 40],
      ['besan', 12],
      ['oil', 6],
    ],
    fat: ['oil'],
    cooked: 55,
    pieces: 1,
    piece: ['serving (55 g)', 'servings (55 g)'],
    units: PIECES,
  },
  {
    id: 'veg-thali',
    name: 'Veg thali',
    aliases: ['north indian thali', 'mess thali', 'lunch thali'],
    category: 'sabzi',
    region: 'north',
    meals: ['lunch', 'dinner'],
    lines: [
      ['atta', 50],
      ['toor-dal', 30],
      ['oil', 13],
      ['rice', 50],
      ['cauliflower', 80],
      ['potato', 40],
      ['curd', 60],
      ['cucumber', 30],
    ],
    fat: ['oil'],
    cooked: 560,
    pieces: 1,
    piece: ['thali (2 roti, dal, sabzi, rice, curd)', 'thalis (2 roti, dal, sabzi, rice, curd)'],
    units: PIECES,
  },
  {
    id: 'chicken-thukpa',
    name: 'Chicken thukpa',
    aliases: ['thukpa', 'thenthuk', 'himalayan noodle soup'],
    category: 'chicken',
    region: 'east',
    meals: ['lunch', 'dinner'],
    lines: [
      ['maida', 70],
      ['chicken', 60],
      ['carrot', 20],
      ['cabbage', 30],
      ['onion', 20],
      ['oil', 8],
      ['water', 300],
    ],
    fat: ['oil'],
    cooked: 450,
    pieces: 1,
    piece: ['bowl', 'bowls'],
    units: PIECES,
  },
  {
    id: 'idiyappam',
    name: 'Idiyappam',
    aliases: ['string hoppers', 'nool puttu', 'sevai', 'idiappam'],
    category: 'south',
    region: 'south',
    meals: ['breakfast', 'dinner'],
    lines: [
      ['rice-flour', 50],
      ['coconut', 10],
    ],
    cooked: 150,
    pieces: 3,
    piece: ['idiyappam', 'idiyappams'],
    units: PIECES,
    amount: 3,
  },
  {
    id: 'puttu',
    name: 'Puttu',
    aliases: ['kerala puttu', 'rice puttu'],
    category: 'south',
    region: 'south',
    meals: ['breakfast'],
    lines: [
      ['rice-flour', 60],
      ['coconut', 20],
    ],
    cooked: 150,
    pieces: 1,
    piece: ['cylinder (150 g)', 'cylinders (150 g)'],
    units: PIECES,
  },
  {
    id: 'puran-poli',
    name: 'Puran poli',
    aliases: ['holige', 'obbattu', 'bobbatlu', 'poli'],
    category: 'sweet',
    region: 'west',
    meals: ['lunch', 'snacks'],
    lines: [
      ['atta', 35],
      ['chana-dal', 25],
      ['jaggery', 25],
      ['ghee', 5],
    ],
    cooked: 85,
    pieces: 1,
    piece: ['poli', 'polis'],
    units: PIECES,
  },
  {
    id: 'sandesh',
    name: 'Sandesh',
    aliases: ['bengali sandesh', 'kolkata sandesh'],
    category: 'sweet',
    region: 'east',
    meals: ['snacks'],
    lines: [
      ['paneer', 100],
      ['sugar', 40],
    ],
    cooked: 120,
    pieces: 4,
    piece: ['sandesh', 'sandeshes'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'mysore-pak',
    name: 'Mysore pak',
    aliases: ['mysore paak', 'mysorepak'],
    category: 'sweet',
    region: 'south',
    meals: ['snacks'],
    lines: [
      ['besan', 55],
      ['ghee', 60],
      ['sugar', 85],
    ],
    cooked: 190,
    pieces: 5,
    piece: ['piece', 'pieces'],
    units: PIECES,
  },
  {
    id: 'kulfi',
    name: 'Malai kulfi',
    aliases: ['kulfi', 'matka kulfi', 'kulfi stick'],
    category: 'sweet',
    region: 'north',
    meals: ['snacks'],
    lines: [
      ['milk', 250],
      ['sugar', 30],
      ['milk-powder', 25],
      ['cream', 25],
    ],
    cooked: 250,
    pieces: 3,
    piece: ['kulfi', 'kulfis'],
    units: PIECES,
  },
  {
    id: 'rabri',
    name: 'Rabri',
    aliases: ['rabdi', 'basundi', 'rabri dessert'],
    category: 'sweet',
    region: 'north',
    meals: ['snacks', 'dinner'],
    lines: [
      ['milk', 400],
      ['sugar', 25],
    ],
    cooked: 150,
    units: ['katori', 'g'],
    density: 1.1,
  },
  {
    id: 'shrikhand',
    name: 'Shrikhand',
    aliases: ['srikhand', 'amrakhand'],
    category: 'sweet',
    region: 'west',
    meals: ['snacks', 'lunch'],
    lines: [
      ['curd', 300],
      ['sugar', 70],
    ],
    cooked: 220,
    units: ['katori', 'g'],
    density: 1.1,
  },
  {
    id: 'malpua',
    name: 'Malpua',
    aliases: ['malpua with syrup', 'malpuwa'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['maida', 40],
      ['milk', 40],
      ['sugar', 35],
      ['ghee', 15],
    ],
    fat: ['ghee'],
    cooked: 110,
    pieces: 2,
    piece: ['malpua', 'malpuas'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'peda',
    name: 'Milk peda',
    aliases: ['peda', 'mathura peda', 'doodh peda'],
    category: 'sweet',
    region: 'north',
    meals: ['snacks'],
    lines: [
      ['milk-powder', 50],
      ['ghee', 8],
      ['sugar', 25],
    ],
    fat: ['ghee'],
    cooked: 85,
    pieces: 4,
    piece: ['peda', 'pedas'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'rasmalai',
    name: 'Rasmalai',
    aliases: ['ras malai', 'rasmalai dessert'],
    category: 'sweet',
    region: 'east',
    meals: ['snacks'],
    lines: [
      ['paneer', 60],
      ['milk', 200],
      ['sugar', 35],
    ],
    cooked: 250,
    pieces: 2,
    piece: ['piece with rabri', 'pieces with rabri'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'mutton-keema',
    name: 'Mutton keema',
    aliases: ['keema', 'kheema', 'keema matar', 'minced mutton'],
    category: 'meat',
    region: 'north',
    meals: ['lunch', 'dinner'],
    lines: [
      ['mutton', 150],
      ['onion', 60],
      ['tomato', 60],
      ['oil', 18],
      ['peas', 30],
    ],
    fat: ['oil'],
    cooked: 300,
    units: ['katori', 'g'],
    density: 1.0,
  },
  {
    id: 'sugarcane-juice',
    name: 'Sugarcane juice',
    aliases: ['ganne ka ras', 'ganne ka juice', 'cane juice', 'karumbu juice'],
    category: 'drink',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['sugar', 40],
      ['water', 210],
    ],
    cooked: 250,
    servings: 1,
    units: ['glass', 'g'],
    density: 1.05,
  },
  {
    id: 'matcha-latte',
    name: 'Matcha latte',
    aliases: ['matcha', 'green tea latte', 'iced matcha'],
    category: 'drink',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['milk', 200],
      ['sugar', 10],
      ['tea', 2],
    ],
    cooked: 215,
    servings: 1,
    units: ['glass', 'g'],
    density: 1.03,
  },
  {
    id: 'kombucha',
    name: 'Kombucha',
    aliases: ['kombucha tea', 'fermented tea'],
    category: 'drink',
    region: 'all',
    meals: ['snacks'],
    lines: [
      ['sugar', 7],
      ['water', 233],
    ],
    cooked: 240,
    servings: 1,
    units: ['glass', 'g'],
    density: 1.0,
  },
  {
    id: 'almond-milk',
    name: 'Almond milk (unsweetened)',
    aliases: ['badam milk unsweetened', 'plant milk'],
    category: 'drink',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['almonds', 3],
      ['water', 97],
    ],
    cooked: 100,
    servings: 1,
    units: ['glass', 'g'],
    amount: 1,
    density: 1.0,
  },
  {
    id: 'oat-milk',
    name: 'Oat milk (unsweetened)',
    aliases: ['oats milk', 'plant milk', 'oatly'],
    category: 'drink',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['oats', 12],
      ['water', 88],
    ],
    cooked: 100,
    servings: 1,
    units: ['glass', 'g'],
    amount: 1,
    density: 1.0,
  },
  {
    id: 'khakhra',
    name: 'Khakhra',
    aliases: ['plain khakhra', 'gujarati khakhra', 'methi khakhra'],
    category: 'snack',
    region: 'west',
    meals: ['breakfast', 'snacks'],
    lines: [
      ['atta', 30],
      ['oil', 3],
    ],
    fat: ['oil'],
    cooked: 33,
    pieces: 2,
    piece: ['khakhra', 'khakhras'],
    units: PIECES,
    amount: 2,
  },
  {
    id: 'murukku',
    name: 'Murukku',
    aliases: ['chakli', 'chakri', 'chakkli', 'rice murukku'],
    category: 'snack',
    region: 'south',
    meals: ['snacks'],
    lines: [
      ['rice-flour', 60],
      ['urad-dal', 10],
      ['oil', 22],
      ['sesame', 2],
    ],
    fat: ['oil'],
    cooked: 95,
    pieces: 6,
    piece: ['murukku', 'murukkus'],
    units: PIECES,
    amount: 3,
  },
];

const BASICS: BasicDef[] = [
  {
    id: 'cheesecake',
    name: 'Cheesecake',
    aliases: ['blueberry cheesecake', 'new york cheesecake', 'baked cheesecake'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    ingredient: 'cheesecake',
    units: PIECES,
    pieceG: 100,
    piece: ['slice', 'slices'],
  },
  {
    id: 'brownie',
    name: 'Chocolate brownie',
    aliases: ['brownie', 'walnut brownie', 'fudge brownie'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    ingredient: 'brownie',
    units: PIECES,
    pieceG: 60,
    piece: ['brownie', 'brownies'],
  },
  {
    id: 'choc-chip-cookie',
    name: 'Chocolate chip cookie',
    aliases: ['cookie', 'cookies', 'choco chip cookie'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    ingredient: 'choc-chip-cookie',
    units: PIECES,
    pieceG: 40,
    piece: ['cookie (cafe size)', 'cookies (cafe size)'],
  },
  {
    id: 'glazed-doughnut',
    name: 'Glazed doughnut',
    aliases: ['donut', 'doughnut', 'glazed donut'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    ingredient: 'glazed-doughnut',
    units: PIECES,
    pieceG: 60,
    piece: ['doughnut', 'doughnuts'],
  },
  {
    id: 'croissant',
    name: 'Butter croissant',
    aliases: ['croissant', 'croissants'],
    category: 'breakfast',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    ingredient: 'croissant',
    units: PIECES,
    pieceG: 60,
    piece: ['croissant', 'croissants'],
  },
  {
    id: 'vanilla-ice-cream',
    name: 'Ice cream, vanilla',
    aliases: ['ice cream', 'vanilla ice cream', 'gelato', 'scoop of ice cream'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks', 'dinner'],
    ingredient: 'ice-cream',
    units: PIECES,
    pieceG: 66,
    piece: ['scoop', 'scoops'],
  },
  {
    id: 'chocolate-ice-cream',
    name: 'Ice cream, chocolate',
    aliases: ['chocolate ice cream', 'gelato'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks', 'dinner'],
    ingredient: 'choc-ice-cream',
    units: PIECES,
    pieceG: 66,
    piece: ['scoop', 'scoops'],
  },
  {
    id: 'dark-chocolate',
    name: 'Dark chocolate',
    aliases: ['chocolate', 'dark choc'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    ingredient: 'dark-chocolate',
    units: ['g', 'piece'],
    pieceG: 10,
    piece: ['square', 'squares'],
    amount: 20,
  },
  {
    id: 'milk-chocolate',
    name: 'Milk chocolate',
    aliases: ['chocolate', 'chocolate bar', 'dairy milk'],
    category: 'sweet',
    region: 'all',
    meals: ['snacks'],
    ingredient: 'milk-chocolate',
    units: ['g', 'piece'],
    pieceG: 10,
    piece: ['square', 'squares'],
    amount: 25,
  },
  {
    id: 'hummus',
    name: 'Hummus',
    aliases: ['hummus dip'],
    category: 'side',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'hummus',
    units: ['tbsp', 'katori', 'g'],
    amount: 2,
    density: 1,
  },
  {
    id: 'avocado',
    name: 'Avocado',
    aliases: ['butter fruit', 'avocado'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'avocado',
    units: PIECES,
    pieceG: 136,
    piece: ['avocado (without stone)', 'avocados'],
  },
  {
    id: 'granola',
    name: 'Granola',
    aliases: ['granola', 'crunchy muesli'],
    category: 'breakfast',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    ingredient: 'granola',
    units: ['g'],
    amount: 40,
  },
  {
    id: 'strawberries',
    name: 'Strawberries',
    aliases: ['strawberry'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'strawberries',
    units: ['katori', 'g'],
    density: 0.6,
  },
  {
    id: 'blueberries',
    name: 'Blueberries',
    aliases: ['blueberry'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'blueberries',
    units: ['katori', 'g'],
    density: 0.6,
  },
  {
    id: 'pita-bread',
    name: 'Pita bread',
    aliases: ['pita', 'pitta'],
    category: 'bread',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'pita',
    units: PIECES,
    pieceG: 60,
    piece: ['pita', 'pitas'],
  },
  // Gym staples: what people who train eat most, plain and simple to log.
  {
    id: 'egg-white',
    name: 'Egg whites',
    aliases: ['egg white', 'anda safedi', 'whites'],
    category: 'egg',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'egg-white',
    units: PIECES,
    pieceG: 33,
    piece: ['egg white', 'egg whites'],
    amount: 4,
  },
  {
    id: 'chicken-breast',
    name: 'Chicken breast, grilled',
    aliases: ['chicken breast', 'grilled chicken', 'boiled chicken breast', 'chicken'],
    category: 'chicken',
    region: 'all',
    meals: MAINS,
    ingredient: 'chicken-breast-cooked',
    units: ['g', 'piece'],
    pieceG: 120,
    piece: ['breast (120 g)', 'breasts (120 g)'],
    amount: 150,
  },
  {
    id: 'brown-rice',
    name: 'Brown rice, cooked',
    aliases: ['brown rice'],
    category: 'rice',
    region: 'all',
    meals: MAINS,
    ingredient: 'brown-rice-cooked',
    units: ['katori', 'g'],
    density: 0.66,
  },
  {
    id: 'quinoa',
    name: 'Quinoa, cooked',
    aliases: ['quinoa'],
    category: 'rice',
    region: 'all',
    meals: MAINS,
    ingredient: 'quinoa-cooked',
    units: ['katori', 'g'],
    density: 0.75,
  },
  {
    id: 'sweet-potato',
    name: 'Sweet potato, baked or boiled',
    aliases: ['shakarkandi', 'sweet potato', 'shakarkand'],
    category: 'basic',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'sweet-potato-baked',
    units: ['piece', 'g'],
    pieceG: 150,
    piece: ['medium sweet potato', 'medium sweet potatoes'],
  },
  {
    id: 'dates',
    name: 'Dates',
    aliases: ['khajur', 'khajoor', 'dates'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'dates',
    units: PIECES,
    pieceG: 8,
    piece: ['date', 'dates'],
    amount: 3,
  },
  {
    id: 'tuna',
    name: 'Tuna, canned in water',
    aliases: ['tuna'],
    category: 'fish',
    region: 'all',
    meals: MAINS,
    ingredient: 'tuna-canned',
    units: ['g'],
    amount: 100,
  },
  {
    id: 'milk-skimmed',
    name: 'Milk, skimmed',
    aliases: ['skimmed milk', 'skim milk', 'double toned milk', 'fat free milk'],
    category: 'dairy',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'milk-skimmed',
    units: ['glass', 'cup', 'g'],
    density: 1.03,
  },
  {
    id: 'greek-yogurt',
    name: 'Greek yogurt / hung curd',
    aliases: ['greek yogurt', 'hung curd', 'chakka', 'high protein curd'],
    category: 'dairy',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'greek-yogurt',
    units: ['katori', 'g'],
    density: 1.05,
  },
  {
    id: 'brown-bread',
    name: 'Brown bread',
    aliases: ['whole wheat bread', 'atta bread', 'multigrain bread'],
    category: 'bread',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    ingredient: 'brown-bread',
    units: PIECES,
    pieceG: 28,
    piece: ['slice', 'slices'],
    amount: 2,
  },
  {
    id: 'kala-chana-boiled',
    name: 'Chana, boiled',
    aliases: ['boiled chana', 'kala chana', 'boiled chickpeas', 'chana salad'],
    category: 'snack',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'chickpeas-boiled',
    units: ['katori', 'g'],
    density: 0.7,
  },
  {
    id: 'rajma-boiled',
    name: 'Rajma, boiled',
    aliases: ['boiled rajma', 'kidney beans'],
    category: 'snack',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'rajma-boiled',
    units: ['katori', 'g'],
    density: 0.75,
  },
  {
    id: 'tofu',
    name: 'Tofu',
    aliases: ['soya paneer', 'tofu'],
    category: 'basic',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'tofu',
    units: ['g'],
    amount: 100,
  },
  {
    id: 'curd',
    name: 'Curd',
    aliases: ['dahi', 'yogurt', 'yoghurt', 'thayir', 'mosaru'],
    category: 'dairy',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'curd',
    units: ['katori', 'g'],
    density: 1.02,
  },
  {
    id: 'milk',
    name: 'Milk, full cream',
    aliases: ['milk', 'doodh', 'whole milk', 'cow milk'],
    category: 'dairy',
    region: 'all',
    meals: ['breakfast', 'snacks', 'dinner'],
    ingredient: 'milk',
    units: ['glass', 'cup', 'g'],
    density: 1.03,
  },
  {
    id: 'milk-toned',
    name: 'Milk, toned',
    aliases: ['toned milk', 'doodh', 'low fat milk'],
    category: 'dairy',
    region: 'all',
    meals: ['breakfast', 'snacks', 'dinner'],
    ingredient: 'milk-toned',
    units: ['glass', 'cup', 'g'],
    density: 1.03,
  },
  {
    id: 'milk-buffalo',
    name: 'Milk, buffalo',
    aliases: ['bhains ka doodh', 'buffalo milk'],
    category: 'dairy',
    region: 'all',
    meals: ['breakfast', 'snacks', 'dinner'],
    ingredient: 'milk-buffalo',
    units: ['glass', 'cup', 'g'],
    density: 1.03,
  },
  {
    id: 'paneer',
    name: 'Paneer, plain',
    aliases: ['paneer', 'cottage cheese', 'raw paneer', 'chhena'],
    category: 'dairy',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'paneer',
    units: ['g', 'piece'],
    pieceG: 25,
    piece: ['cube', 'cubes'],
    amount: 100,
  },
  {
    id: 'ghee',
    name: 'Ghee',
    aliases: ['clarified butter', 'desi ghee'],
    category: 'basic',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'ghee',
    units: ['tsp', 'tbsp', 'g'],
    density: 0.91,
  },
  {
    id: 'butter',
    name: 'Butter',
    aliases: ['makhan'],
    category: 'basic',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'butter',
    units: ['tsp', 'tbsp', 'g'],
    density: 0.95,
  },
  {
    id: 'sugar',
    name: 'Sugar',
    aliases: ['cheeni', 'shakkar'],
    category: 'basic',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'sugar',
    units: ['tsp', 'g'],
    density: 0.85,
  },
  {
    id: 'boiled-egg',
    name: 'Boiled egg',
    aliases: ['egg', 'anda', 'ubla anda', 'eggs'],
    category: 'egg',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'egg',
    units: PIECES,
    pieceG: 50,
    piece: ['egg', 'eggs'],
    amount: 2,
  },
  {
    id: 'bread-slice',
    name: 'White bread',
    aliases: ['bread', 'double roti', 'toast'],
    category: 'bread',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    ingredient: 'bread',
    units: PIECES,
    pieceG: 25,
    piece: ['slice', 'slices'],
    amount: 2,
  },
  {
    id: 'pav',
    name: 'Pav',
    aliases: ['pao', 'ladi pav', 'bun'],
    category: 'bread',
    region: 'west',
    meals: ['breakfast', 'snacks', 'dinner'],
    ingredient: 'bread',
    units: PIECES,
    pieceG: 40,
    piece: ['pav', 'pav'],
    amount: 2,
  },
  {
    id: 'peanuts',
    name: 'Peanuts',
    aliases: ['moongphali', 'groundnut', 'singdana'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    ingredient: 'peanuts',
    units: ['g', 'tbsp'],
    amount: 30,
    density: 0.6,
  },
  {
    id: 'roasted-chana',
    name: 'Roasted chana',
    aliases: ['bhuna chana', 'chana', 'futana'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    ingredient: 'kabuli-chana',
    units: ['g', 'tbsp'],
    amount: 30,
    density: 0.65,
  },
  {
    id: 'almonds',
    name: 'Almonds',
    aliases: ['badam'],
    category: 'snack',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    ingredient: 'almonds',
    units: PIECES,
    pieceG: 1.2,
    piece: ['almond', 'almonds'],
    amount: 10,
  },
  {
    id: 'cashews',
    name: 'Cashews',
    aliases: ['kaju'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    ingredient: 'cashews',
    units: PIECES,
    pieceG: 1.5,
    piece: ['cashew', 'cashews'],
    amount: 10,
  },
  {
    id: 'banana',
    name: 'Banana',
    aliases: ['kela', 'bale hannu', 'vazhaipazham'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'banana',
    units: PIECES,
    pieceG: 118,
    piece: ['banana', 'bananas'],
  },
  {
    id: 'apple',
    name: 'Apple',
    aliases: ['seb', 'saib'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'apple',
    units: PIECES,
    pieceG: 182,
    piece: ['apple', 'apples'],
  },
  {
    id: 'mango',
    name: 'Mango',
    aliases: ['aam', 'keri'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'mango',
    units: ['piece', 'katori', 'g'],
    pieceG: 200,
    piece: ['mango', 'mangoes'],
    density: 0.66,
  },
  {
    id: 'papaya',
    name: 'Papaya',
    aliases: ['papita'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'papaya',
    units: ['katori', 'g'],
    density: 0.6,
  },
  {
    id: 'orange',
    name: 'Orange',
    aliases: ['santra', 'narangi', 'kinnow'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'orange',
    units: PIECES,
    pieceG: 131,
    piece: ['orange', 'oranges'],
  },
  {
    id: 'guava',
    name: 'Guava',
    aliases: ['amrood', 'peru'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'guava',
    units: PIECES,
    pieceG: 55,
    piece: ['guava', 'guavas'],
    amount: 2,
  },
  {
    id: 'watermelon',
    name: 'Watermelon',
    aliases: ['tarbooz', 'kalingad'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'watermelon',
    units: ['katori', 'g'],
    density: 0.64,
  },
  {
    id: 'grapes',
    name: 'Grapes',
    aliases: ['angoor'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'grapes',
    units: ['katori', 'g'],
    density: 0.64,
  },
  {
    id: 'pomegranate',
    name: 'Pomegranate',
    aliases: ['anar'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'pomegranate',
    units: ['katori', 'g'],
    density: 0.73,
  },
  // Fruit, nuts, seeds and drinks added October 2026
  {
    id: 'kiwi',
    name: 'Kiwi',
    aliases: ['kiwi fruit', 'kiwifruit'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'kiwi',
    units: PIECES,
    pieceG: 75,
    piece: ['kiwi', 'kiwis'],
    amount: 2,
  },
  {
    id: 'lychee',
    name: 'Lychee',
    aliases: ['litchi', 'leechi'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'lychee',
    units: PIECES,
    pieceG: 10,
    piece: ['lychee', 'lychees'],
    amount: 8,
  },
  {
    id: 'jackfruit',
    name: 'Jackfruit',
    aliases: ['kathal', 'ripe jackfruit', 'chakka', 'panas'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'jackfruit',
    units: ['katori', 'g'],
    density: 0.6,
  },
  {
    id: 'sapota',
    name: 'Sapota (chikoo)',
    aliases: ['chikoo', 'chiku', 'sapodilla', 'chikku'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'sapota',
    units: PIECES,
    pieceG: 100,
    piece: ['chikoo', 'chikoos'],
  },
  {
    id: 'custard-apple',
    name: 'Custard apple (sitaphal)',
    aliases: ['sitaphal', 'sharifa', 'seetaphal', 'sugar apple', 'sweetsop'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'custard-apple',
    units: PIECES,
    pieceG: 125,
    piece: ['custard apple (pulp)', 'custard apples (pulp)'],
  },
  {
    id: 'pineapple',
    name: 'Pineapple',
    aliases: ['ananas', 'pineapple slices'],
    category: 'fruit',
    region: 'all',
    meals: ALL_MEALS,
    ingredient: 'pineapple',
    units: ['katori', 'g'],
    density: 0.7,
  },
  {
    id: 'walnuts',
    name: 'Walnuts',
    aliases: ['akhrot', 'akhrod', 'walnut'],
    category: 'snack',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    ingredient: 'walnuts',
    units: PIECES,
    pieceG: 5,
    piece: ['walnut half', 'walnut halves'],
    amount: 4,
  },
  {
    id: 'pistachios',
    name: 'Pistachios',
    aliases: ['pista', 'pistachio', 'pista nuts'],
    category: 'snack',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    ingredient: 'pistachios',
    units: PIECES,
    pieceG: 0.8,
    piece: ['pistachio', 'pistachios'],
    amount: 25,
  },
  {
    id: 'flax-seeds',
    name: 'Flax seeds',
    aliases: ['alsi', 'flaxseed', 'linseed', 'avisa ginjalu'],
    category: 'basic',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    ingredient: 'flaxseed',
    units: ['g'],
    amount: 10,
  },
  {
    id: 'pumpkin-seeds',
    name: 'Pumpkin seeds',
    aliases: ['kaddu ke beej', 'pepitas', 'pumpkin seed'],
    category: 'basic',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    ingredient: 'pumpkin-seeds',
    units: ['g'],
    amount: 15,
  },
  {
    id: 'sunflower-seeds',
    name: 'Sunflower seeds',
    aliases: ['surajmukhi beej', 'sunflower seed'],
    category: 'basic',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    ingredient: 'sunflower-seeds',
    units: ['g'],
    amount: 15,
  },
  {
    id: 'popcorn',
    name: 'Popcorn (air-popped, plain)',
    aliases: ['popcorn', 'pop corn', 'makka popcorn'],
    category: 'snack',
    region: 'all',
    meals: ['snacks'],
    ingredient: 'popcorn',
    units: ['katori', 'g'],
    density: 0.08,
    amount: 2,
  },
  {
    id: 'tender-coconut-water',
    name: 'Tender coconut water',
    aliases: ['nariyal pani', 'coconut water', 'elaneer', 'daab', 'coconut drink'],
    category: 'drink',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    ingredient: 'coconut-water',
    units: ['glass', 'g'],
    density: 1.02,
  },
  {
    id: 'soy-milk',
    name: 'Soy milk',
    aliases: ['soya milk', 'soymilk', 'plant milk'],
    category: 'drink',
    region: 'all',
    meals: ['breakfast', 'snacks'],
    ingredient: 'soymilk',
    units: ['glass', 'cup', 'g'],
    density: 1.03,
  },
];

/**
 * Packaged foods people log most, from the values printed on their labels (read through
 * FatSecret India, Open Food Facts and the makers' sites, October 2026). `serving` is the
 * label's own serving; values are per 100 g unless `perServing` is set.
 */
export interface LabelDef {
  id: string;
  name: string;
  aliases?: string[];
  category: FoodCategory;
  diet?: Food['diet'];
  /** kcal, protein, carbs, fat, fibre. */
  values: [number, number, number, number, number];
  /** The values above are for one serving of this many grams, not 100 g. */
  perServing?: number;
  serving?: { g: number; name: [string, string] };
  amount?: number;
  meals?: Meal[];
  /** A restaurant chain's published nutrition, not a pack label. */
  chain?: boolean;
}

const LABELS: LabelDef[] = [
  // Whey, plant and yeast protein (one scoop as on the label)
  {
    id: 'on-gold-standard-whey',
    name: 'Optimum Nutrition Gold Standard 100% Whey',
    aliases: ['on whey', 'gold standard', 'optimum nutrition', 'whey protein'],
    category: 'drink',
    values: [120, 24, 3, 1.5, 0],
    perServing: 30.4,
    serving: { g: 30.4, name: ['scoop', 'scoops'] },
  },
  {
    id: 'muscleblaze-biozyme-whey',
    name: 'MuscleBlaze Biozyme Performance Whey',
    aliases: ['muscleblaze', 'mb whey', 'biozyme', 'whey protein'],
    category: 'drink',
    values: [140, 25, 5.65, 1.8, 0],
    perServing: 36,
    serving: { g: 36, name: ['scoop', 'scoops'] },
  },
  {
    id: 'avvatar-whey',
    name: 'Avvatar Whey Protein',
    aliases: ['avvatar', 'whey protein'],
    category: 'drink',
    values: [141, 27.6, 3, 2.1, 0],
    perServing: 35,
    serving: { g: 35, name: ['scoop', 'scoops'] },
  },
  {
    id: 'myprotein-impact-whey',
    name: 'Myprotein Impact Whey Protein',
    aliases: ['myprotein', 'impact whey', 'whey protein'],
    category: 'drink',
    values: [100, 19, 1.9, 1.9, 0.4],
    perServing: 25,
    serving: { g: 25, name: ['scoop', 'scoops'] },
  },
  {
    id: 'asitis-whey-concentrate',
    name: 'AS-IT-IS Whey Protein Concentrate 80%',
    aliases: ['asitis', 'as it is', 'raw whey', 'whey protein'],
    category: 'drink',
    values: [141, 28, 2.9, 1.8, 0],
    perServing: 35,
    serving: { g: 35, name: ['scoop', 'scoops'] },
  },
  {
    id: 'oziva-plant-protein',
    name: 'OZiva Organic Plant Protein',
    aliases: ['oziva', 'plant protein', 'vegan protein'],
    category: 'drink',
    values: [140, 30, 3.8, 0.45, 0],
    perServing: 36,
    serving: { g: 36, name: ['scoop', 'scoops'] },
  },
  {
    id: 'plix-plant-protein',
    name: 'Plix Plant Protein',
    aliases: ['plix', 'plant protein', 'vegan protein'],
    category: 'drink',
    values: [108, 25, 5, 0.6, 0],
    perServing: 34,
    serving: { g: 34, name: ['scoop', 'scoops'] },
  },
  {
    id: 'muscleblaze-plant-protein',
    name: 'MuscleBlaze Plant Protein',
    aliases: ['muscleblaze plant', 'plant protein', 'vegan protein'],
    category: 'drink',
    values: [144, 24, 5.1, 2.74, 0],
    perServing: 36.5,
    serving: { g: 36.5, name: ['scoop', 'scoops'] },
  },
  // The label gives energy and protein only; the rest of the energy is shown as carbs.
  {
    id: 'yogabar-yeast-protein',
    name: 'Yogabar Yeast Protein',
    aliases: ['yeast protein', 'yoga bar protein', 'yogabar'],
    category: 'drink',
    values: [148.8, 30.8, 6.4, 0, 0],
    perServing: 38,
    serving: { g: 38, name: ['scoop', 'scoops'] },
  },
  // More protein powders (October 2026), each from its printed label or a listing of it.
  {
    id: 'naturaltein-whey',
    name: 'Naturaltein Whey Protein Concentrate',
    aliases: ['naturaltein', 'whey protein', 'whey'],
    category: 'drink',
    values: [110, 21.8, 2.4, 1.6, 0],
    perServing: 30,
    serving: { g: 30, name: ['scoop', 'scoops'] },
  },
  {
    id: 'naturaltein-whey-isolate',
    name: 'Naturaltein Whey Protein Isolate',
    aliases: ['naturaltein isolate', 'whey isolate', 'whey protein', 'whey'],
    category: 'drink',
    values: [119, 24, 1.6, 1.8, 0],
    perServing: 30,
    serving: { g: 30, name: ['scoop', 'scoops'] },
  },
  {
    id: 'whole-truth-whey-isolate',
    name: 'The Whole Truth Whey Protein Isolate',
    aliases: ['whole truth whey', 'twt whey', 'whey isolate', 'whey protein', 'whey'],
    category: 'drink',
    values: [132, 30.4, 1.1, 0.7, 0],
    perServing: 35,
    serving: { g: 35, name: ['scoop', 'scoops'] },
  },
  {
    id: 'whole-truth-whey-cocoa',
    name: 'The Whole Truth Whey Protein Light Cocoa',
    aliases: ['whole truth whey', 'twt whey', 'cocoa whey', 'whey protein', 'whey'],
    category: 'drink',
    values: [139, 24.9, 6.4, 1.5, 0],
    perServing: 35,
    serving: { g: 35, name: ['scoop', 'scoops'] },
  },
  {
    id: 'dymatize-elite-whey',
    name: 'Dymatize Elite 100% Whey',
    aliases: ['dymatize', 'elite whey', 'whey protein', 'whey'],
    category: 'drink',
    values: [130, 25, 3, 1.5, 0],
    perServing: 34.9,
    serving: { g: 34.9, name: ['scoop', 'scoops'] },
  },
  {
    id: 'muscletech-nitrotech-gold',
    name: 'MuscleTech Nitro-Tech Whey Gold',
    aliases: ['muscletech', 'nitrotech', 'nitro tech', 'whey protein', 'whey'],
    category: 'drink',
    values: [120, 24, 2, 2, 0],
    perServing: 33,
    serving: { g: 33, name: ['scoop', 'scoops'] },
  },
  {
    id: 'nakpro-perform-whey',
    name: 'Nakpro Perform Whey Protein Concentrate',
    aliases: ['nakpro', 'whey protein', 'whey'],
    category: 'drink',
    values: [126, 24, 4.6, 1.8, 0],
    perServing: 33,
    serving: { g: 33, name: ['scoop', 'scoops'] },
  },
  {
    id: 'beastlife-whey',
    name: 'BeastLife Whey Protein',
    aliases: ['beastlife', 'beast life', 'whey protein', 'whey'],
    category: 'drink',
    values: [135, 24.27, 4.15, 2.4, 0],
    perServing: 35,
    serving: { g: 35, name: ['scoop', 'scoops'] },
  },
  {
    id: 'superyou-yeast-protein',
    name: 'SuperYou Pro Fermented Yeast Protein',
    aliases: ['superyou', 'super you', 'yeast protein', 'vegan protein'],
    category: 'drink',
    values: [134, 27.03, 2.15, 1.92, 0],
    perServing: 33,
    serving: { g: 33, name: ['scoop', 'scoops'] },
  },
  {
    id: 'mille-yeast-protein',
    name: 'Mille Yeast Protein',
    aliases: ['mille', 'yeast protein', 'vegan protein'],
    category: 'drink',
    values: [141, 25.2, 5.2, 2.6, 0],
    perServing: 38,
    serving: { g: 38, name: ['scoop', 'scoops'] },
  },
  {
    id: 'naturaltein-yeast-protein',
    name: 'Naturaltein Yeast Protein',
    aliases: ['naturaltein yeast', 'yeast protein', 'vegan protein'],
    category: 'drink',
    values: [398, 73.58, 8.28, 7.81, 0],
    serving: { g: 30, name: ['scoop', 'scoops'] },
  },
  {
    id: 'nutrabay-yeast-protein',
    name: 'Nutrabay Fermented Yeast Protein',
    aliases: ['nutrabay', 'yeast protein', 'vegan protein'],
    category: 'drink',
    values: [134, 25.2, 6.23, 1.47, 0],
    perServing: 36,
    serving: { g: 36, name: ['scoop', 'scoops'] },
  },
  // Cosmix and Plix yeast proteins: the labels found give protein, carbs and fat per 100 g, so energy is 4, 4 and 9 kcal a gram of them.
  {
    id: 'cosmix-yeast-protein',
    name: 'Cosmix No-Nonsense Fermented Yeast Protein',
    aliases: ['cosmix yeast', 'cosmix', 'yeast protein', 'vegan protein'],
    category: 'drink',
    values: [390, 64, 29, 2, 0],
    serving: { g: 38, name: ['sachet or scoop', 'sachets or scoops'] },
  },
  {
    id: 'plix-yeast-protein',
    name: 'Plix Super Strength Fermented Yeast Protein',
    aliases: ['plix yeast', 'plix', 'yeast protein', 'vegan protein'],
    category: 'drink',
    values: [377, 68, 15, 5, 0],
    serving: { g: 35, name: ['scoop', 'scoops'] },
  },
  {
    id: 'gnc-yeast-protein',
    name: 'GNC Select Fermented Yeast Protein',
    aliases: ['gnc', 'gnc select', 'yeast protein', 'vegan protein'],
    category: 'drink',
    values: [145, 25, 6.2, 1.68, 0],
    perServing: 35,
    serving: { g: 35, name: ['scoop', 'scoops'] },
  },
  {
    id: 'cosmix-plant-protein',
    name: 'Cosmix No-Nonsense Plant Protein',
    aliases: ['cosmix plant', 'cosmix', 'plant protein', 'vegan protein'],
    category: 'drink',
    values: [144, 23.56, 10.19, 0.87, 0],
    perServing: 38,
    serving: { g: 38, name: ['scoop', 'scoops'] },
  },
  {
    id: 'whole-truth-plant-protein',
    name: 'The Whole Truth Plant Protein Double Cocoa',
    aliases: ['whole truth plant', 'twt plant', 'plant protein', 'vegan protein'],
    category: 'drink',
    values: [150, 25.4, 5.3, 3, 0],
    perServing: 38,
    serving: { g: 38, name: ['scoop', 'scoops'] },
  },
  // Origin Nutrition: protein from an independent lab test (the label matched it), carbs and fat as labelled; energy worked out.
  {
    id: 'origin-plant-protein',
    name: 'Origin Nutrition Plant Protein',
    aliases: ['origin nutrition', 'origin', 'plant protein', 'vegan protein'],
    category: 'drink',
    values: [372, 78, 15, 0, 0],
    serving: { g: 32, name: ['scoop', 'scoops'] },
  },
  {
    id: 'nutrabox-soy-isolate',
    name: 'Nutrabox Soy Protein Isolate',
    aliases: ['nutrabox soy', 'soy protein', 'soy isolate'],
    category: 'drink',
    values: [133, 30, 2.81, 0.2, 0],
    perServing: 37,
    serving: { g: 37, name: ['scoop', 'scoops'] },
  },
  {
    id: 'nutrabox-vegan-protein',
    name: 'Nutrabox Vegan Protein',
    aliases: ['nutrabox', 'plant protein', 'vegan protein'],
    category: 'drink',
    values: [138, 25, 3, 0.5, 0],
    perServing: 36,
    serving: { g: 36, name: ['scoop', 'scoops'] },
  },
  // AS-IT-IS soy: the label gives protein, carbs and fat a 35 g scoop; energy worked out.
  {
    id: 'asitis-soy-isolate',
    name: 'AS-IT-IS Soy Protein Isolate',
    aliases: ['asitis soy', 'as it is soy', 'soy protein', 'soy isolate'],
    category: 'drink',
    values: [132, 30, 2, 0.48, 0],
    perServing: 35,
    serving: { g: 35, name: ['scoop', 'scoops'] },
  },
  {
    id: 'ace-blend-plant-protein',
    name: 'Ace Blend Intense Plant Protein',
    aliases: ['ace blend', 'plant protein', 'vegan protein'],
    category: 'drink',
    values: [172, 30, 8.32, 2.22, 0],
    perServing: 45,
    serving: { g: 45, name: ['serve', 'serves'] },
  },
  // Wellbeing Nutrition: the label gives energy and protein only; the rest of the energy is shown as carbs.
  {
    id: 'wellbeing-plant-protein',
    name: 'Wellbeing Nutrition Superfood Plant Protein',
    aliases: ['wellbeing nutrition', 'wellbeing', 'plant protein', 'vegan protein'],
    category: 'drink',
    values: [108, 22, 5, 0, 0],
    perServing: 32,
    serving: { g: 32, name: ['scoop', 'scoops'] },
  },
  {
    id: 'nakpro-soy-isolate',
    name: 'Nakpro Platinum Soy Protein Isolate',
    aliases: ['nakpro soy', 'soy protein', 'soy isolate'],
    category: 'drink',
    values: [392, 90, 1, 3.1, 0],
    serving: { g: 30, name: ['scoop', 'scoops'] },
  },
  {
    id: 'nutrela-soya-chunks',
    name: 'Nutrela Soya Chunks (dry)',
    aliases: ['nutrela', 'soya chunks', 'soy protein', 'meal maker'],
    category: 'basic',
    values: [354, 53.19, 33.5, 0.82, 0],
    amount: 30,
    serving: { g: 10, name: ['handful (10 g)', 'handfuls (10 g)'] },
  },
  // Peanut butter (1 tbsp is about 16 g)
  {
    id: 'pintola-peanut-butter',
    name: 'Pintola Classic Peanut Butter',
    aliases: ['pintola', 'peanut butter'],
    category: 'basic',
    values: [605, 26, 22, 44.6, 0],
    serving: { g: 16, name: ['tbsp', 'tbsp'] },
  },
  {
    id: 'pintola-crunchy-peanut-butter',
    name: 'Pintola Crunchy Peanut Butter',
    aliases: ['pintola crunchy', 'peanut butter'],
    category: 'basic',
    values: [639, 30, 18, 49, 0],
    serving: { g: 16, name: ['tbsp', 'tbsp'] },
  },
  {
    id: 'pintola-high-protein-peanut-butter',
    name: 'Pintola High Protein Peanut Butter',
    aliases: ['pintola high protein', 'peanut butter'],
    category: 'basic',
    values: [583, 30, 27, 38, 0],
    serving: { g: 16, name: ['tbsp', 'tbsp'] },
  },
  {
    id: 'myfitness-chocolate-peanut-butter',
    name: 'MyFitness Chocolate Peanut Butter',
    aliases: ['myfitness', 'peanut butter'],
    category: 'basic',
    values: [628, 28, 17.5, 49.6, 0],
    serving: { g: 16, name: ['tbsp', 'tbsp'] },
  },
  {
    id: 'alpino-natural-peanut-butter',
    name: 'Alpino Natural Peanut Butter',
    aliases: ['alpino', 'peanut butter'],
    category: 'basic',
    values: [645, 29, 17, 52, 0],
    serving: { g: 16, name: ['tbsp', 'tbsp'] },
  },
  {
    id: 'sundrop-peanut-butter',
    name: 'Sundrop Peanut Butter',
    aliases: ['sundrop', 'peanut butter'],
    category: 'basic',
    values: [647, 26.8, 15.2, 55, 7.8],
    serving: { g: 16, name: ['tbsp', 'tbsp'] },
  },
  // Oats and muesli (dry)
  {
    id: 'quaker-oats',
    name: 'Quaker Oats (dry)',
    aliases: ['quaker', 'oats', 'rolled oats'],
    category: 'breakfast',
    values: [407, 11.8, 68.5, 9.5, 10],
    amount: 40,
    meals: ['breakfast'],
  },
  {
    id: 'quaker-masala-oats',
    name: 'Quaker Instant Masala Oats (dry)',
    aliases: ['quaker masala', 'masala oats'],
    category: 'breakfast',
    values: [385, 9.8, 68.3, 8.1, 0],
    serving: { g: 40, name: ['pack', 'packs'] },
    meals: ['breakfast', 'snacks'],
  },
  {
    id: 'saffola-masala-oats',
    name: 'Saffola Masala Oats Classic Masala (dry)',
    aliases: ['saffola', 'masala oats'],
    category: 'breakfast',
    values: [365, 8.68, 70.53, 7.63, 9.74],
    serving: { g: 38, name: ['pack', 'packs'] },
    meals: ['breakfast', 'snacks'],
  },
  {
    id: 'kelloggs-muesli-fruit-magic',
    name: "Kellogg's Extra Muesli Fruit Magic",
    aliases: ['kelloggs muesli', 'muesli'],
    category: 'breakfast',
    values: [365, 8, 77.3, 5, 0],
    amount: 40,
    meals: ['breakfast'],
  },
  {
    id: 'yogabar-muesli',
    name: 'Yoga Bar Muesli+',
    aliases: ['yoga bar muesli', 'yogabar muesli', 'muesli'],
    category: 'breakfast',
    values: [417, 13, 70, 9.5, 7.5],
    amount: 40,
    meals: ['breakfast'],
  },
  {
    id: 'yogabar-nuts-seeds-muesli',
    name: 'Yoga Bar Fruits, Nuts & Seeds Muesli',
    aliases: ['yoga bar fruits nuts', 'yoga bar seeds', 'muesli'],
    category: 'breakfast',
    values: [367, 9.7, 73, 6.2, 0],
    amount: 40,
    meals: ['breakfast'],
  },
  {
    id: 'bagrrys-protein-muesli',
    name: "Bagrry's Protein Muesli",
    aliases: ['bagrrys', 'protein muesli', 'muesli'],
    category: 'breakfast',
    values: [399, 20.3, 64.5, 8.2, 0],
    amount: 40,
    meals: ['breakfast'],
  },
  {
    id: 'true-elements-muesli',
    name: 'True Elements Muesli No Added Sugar',
    aliases: ['true elements', 'muesli'],
    category: 'breakfast',
    values: [425, 13.5, 68.1, 10.9, 0],
    amount: 40,
    meals: ['breakfast'],
  },
  // High-protein dairy, bars and snacks people in the gym buy
  {
    id: 'epigamia-greek-yogurt',
    name: 'Epigamia Greek Yogurt Natural',
    aliases: ['epigamia', 'greek yogurt'],
    category: 'dairy',
    values: [65, 7, 5, 2, 0],
    amount: 90,
    meals: ALL_MEALS,
  },
  {
    id: 'amul-high-protein-milk',
    name: 'Amul High Protein Milk',
    aliases: ['amul protein milk', 'protein milk'],
    category: 'dairy',
    values: [225, 35, 20, 0.5, 0],
    perServing: 250,
    serving: { g: 250, name: ['pack (250 ml)', 'packs (250 ml)'] },
  },
  {
    id: 'amul-protein-buttermilk',
    name: 'Amul Protein Buttermilk',
    aliases: ['amul buttermilk', 'protein chaas', 'protein buttermilk'],
    category: 'drink',
    values: [54, 7.5, 4, 0.5, 0],
    serving: { g: 200, name: ['pack (200 ml)', 'packs (200 ml)'] },
  },
  {
    id: 'ritebite-max-protein-bar',
    name: 'RiteBite Max Protein Daily Choco Almond',
    aliases: ['max protein', 'ritebite', 'protein bar'],
    category: 'snack',
    values: [179, 10, 27.5, 6.8, 4],
    perServing: 50,
    serving: { g: 50, name: ['bar', 'bars'] },
  },
  {
    id: 'whole-truth-protein-bar',
    name: 'The Whole Truth Peanut Cocoa Protein Bar',
    aliases: ['whole truth', 'twt bar', 'protein bar'],
    category: 'snack',
    values: [348, 20.6, 19.8, 20.7, 0],
    perServing: 67,
    serving: { g: 67, name: ['bar', 'bars'] },
  },
  {
    id: 'fortune-chana-sattu',
    name: 'Fortune Chana Sattu (dry)',
    aliases: ['sattu', 'chana sattu', 'fortune sattu'],
    category: 'basic',
    values: [418, 23, 68, 6, 0],
    amount: 40,
  },
  // Packaged snacks and noodles (the biggest food spend in Indian homes, HCES 2023-24)
  {
    id: 'parle-g',
    name: 'Parle-G biscuits',
    aliases: ['parle g', 'glucose biscuit', 'biscuit'],
    category: 'snack',
    values: [454, 6.9, 77.3, 13, 0],
    serving: { g: 6.6, name: ['biscuit', 'biscuits'] },
    amount: 4,
    meals: ['snacks', 'breakfast'],
  },
  {
    id: 'maggi-masala',
    name: 'Maggi 2-Minute Masala Noodles',
    aliases: ['maggi', 'instant noodles'],
    category: 'snack',
    values: [303, 6.5, 43.6, 10.7, 2.9],
    perServing: 70,
    serving: { g: 70, name: ['pack (cooked)', 'packs (cooked)'] },
    meals: ['snacks', 'breakfast', 'dinner'],
  },
  {
    id: 'haldiram-aloo-bhujia',
    name: "Haldiram's Aloo Bhujia",
    aliases: ['bhujia', 'namkeen', 'haldiram'],
    category: 'snack',
    values: [562, 8.68, 46.1, 38.12, 0.83],
    amount: 30,
    meals: ['snacks'],
  },
  {
    id: 'lays-classic',
    name: "Lay's Classic Salted Chips",
    aliases: ['lays', 'chips', 'potato chips', 'wafers'],
    category: 'snack',
    values: [547, 6.7, 50.7, 34.9, 0],
    amount: 30,
    meals: ['snacks'],
  },
  // Fast food chains in India. Per serving as the chain publishes it (read through FatSecret India,
  // October 2026), so a portion here is the chain's own size. Fibre is not published and stays 0.
  {
    id: 'mcaloo-tikki',
    name: "McDonald's McAloo Tikki",
    aliases: ['mcaloo', 'mc aloo tikki', 'mcd aloo tikki', 'mcdonalds veg burger'],
    category: 'snack',
    diet: 'veg',
    chain: true,
    values: [340, 8.5, 50.27, 11.31, 0],
    perServing: 146,
    serving: { g: 146, name: ['burger', 'burgers'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'mcveggie',
    name: "McDonald's McVeggie",
    aliases: ['mc veggie', 'mcdonalds veggie burger'],
    category: 'snack',
    diet: 'veg',
    chain: true,
    values: [388, 10.24, 56.54, 13.83, 0],
    perServing: 168,
    serving: { g: 168, name: ['burger', 'burgers'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'mcspicy-paneer',
    name: "McDonald's McSpicy Paneer",
    aliases: ['mc spicy paneer', 'mcdonalds paneer burger'],
    category: 'paneer',
    diet: 'veg',
    chain: true,
    values: [647, 20.29, 52.33, 39.45, 0],
    perServing: 199,
    serving: { g: 199, name: ['burger', 'burgers'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'maharaja-mac-chicken',
    name: "McDonald's Chicken Maharaja Mac",
    aliases: ['maharaja mac', 'chicken maharaja', 'mcdonalds maharaja'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [689, 34, 55.39, 36.69, 0],
    perServing: 296,
    serving: { g: 296, name: ['burger', 'burgers'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'mcchicken',
    name: "McDonald's McChicken",
    aliases: ['mc chicken', 'mcdonalds chicken burger'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [397, 15.66, 47.98, 15.7, 0],
    perServing: 173,
    serving: { g: 173, name: ['burger', 'burgers'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'mccrispy-chicken',
    name: "McDonald's McCrispy Chicken",
    aliases: ['mc crispy chicken', 'mcdonalds crispy chicken'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [508, 22.32, 49.38, 27.65, 0],
    perServing: 227,
    serving: { g: 227, name: ['burger', 'burgers'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'mcspicy-chicken',
    name: "McDonald's McSpicy Chicken",
    aliases: ['mc spicy chicken', 'mcdonalds spicy chicken burger'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [450, 21.46, 46.08, 19.36, 0],
    perServing: 186,
    serving: { g: 186, name: ['burger', 'burgers'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'mcd-fries-regular',
    name: "McDonald's Fries regular",
    aliases: ['mcdonalds fries', 'french fries', 'mcd fries'],
    category: 'snack',
    diet: 'veg',
    chain: true,
    values: [216, 3.38, 27.08, 10.39, 0],
    perServing: 77,
    serving: { g: 77, name: ['pack', 'packs'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'mcnuggets-4',
    name: "McDonald's Chicken McNuggets 4 pc",
    aliases: ['chicken nuggets', 'mcnuggets', 'mcdonalds nuggets'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [169, 10.03, 10.5, 9.54, 0],
    perServing: 64,
    serving: { g: 64, name: ['box of 4', 'boxes of 4'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'mcd-veg-surprise',
    name: "McDonald's Veg Surprise",
    aliases: ['veg surprise', 'mcdonalds veg surprise'],
    category: 'snack',
    diet: 'veg',
    chain: true,
    values: [313, 5.71, 39.84, 14.95, 0],
    perServing: 132,
    serving: { g: 132, name: ['burger', 'burgers'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'mcd-pizza-mcpuff',
    name: "McDonald's Pizza McPuff",
    aliases: ['pizza mcpuff', 'mcpuff', 'mcdonalds puff'],
    category: 'snack',
    diet: 'veg',
    chain: true,
    values: [225, 5.45, 24.79, 11.44, 0],
    perServing: 87,
    serving: { g: 87, name: ['puff', 'puffs'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'mcspicy-chicken-wrap',
    name: "McDonald's McSpicy Chicken Wrap",
    aliases: ['mcspicy wrap', 'mcdonalds chicken wrap'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [567, 23.74, 57.06, 26.89, 0],
    perServing: 257,
    serving: { g: 257, name: ['wrap', 'wraps'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'mcgrill-chicken',
    name: "McDonald's Chicken McGrill",
    aliases: ['chicken mcgrill', 'mc grill', 'mcdonalds grilled chicken burger'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [274, 13.19, 36.18, 8.52, 0],
    perServing: 142,
    serving: { g: 142, name: ['burger', 'burgers'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'mcd-cold-coffee',
    name: "McDonald's Cold Coffee 300 ml",
    aliases: ['mcdonalds cold coffee', 'mcd coffee', 'mccafe cold coffee'],
    category: 'drink',
    diet: 'veg',
    chain: true,
    values: [301, 9.75, 40.2, 11.15, 0],
    perServing: 300,
    serving: { g: 300, name: ['cup (300 ml)', 'cups (300 ml)'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'kfc-hot-crispy-1pc',
    name: 'KFC Hot & Crispy 1 pc',
    aliases: ['kfc chicken', 'hot and crispy', 'fried chicken piece', 'kfc fried chicken'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [320, 19, 16, 20, 0],
    perServing: 110,
    serving: { g: 110, name: ['piece', 'pieces'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'kfc-classic-zinger',
    name: 'KFC Classic Zinger Burger',
    aliases: ['zinger burger', 'kfc zinger', 'kfc chicken burger'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [612, 27, 63, 29, 0],
    perServing: 225,
    serving: { g: 225, name: ['burger', 'burgers'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'kfc-popcorn-regular',
    name: 'KFC Popcorn Chicken regular',
    aliases: ['popcorn chicken', 'kfc popcorn', 'chicken popcorn'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [306, 17, 21, 18, 0],
    perServing: 90,
    serving: { g: 90, name: ['regular box', 'regular boxes'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'kfc-boneless-strips-3',
    name: 'KFC Boneless Strips 3 pc',
    aliases: ['kfc strips', 'chicken strips', 'boneless strips'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [351, 27, 23, 22, 0],
    perServing: 135,
    serving: { g: 135, name: ['3 strips', '3 strips'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'kfc-hot-wings-2',
    name: 'KFC Hot Wings 2 pc',
    aliases: ['kfc wings', 'hot wings', 'chicken wings'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [249, 11, 16, 16, 0],
    perServing: 70,
    serving: { g: 70, name: ['2 wings', '2 wings'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'kfc-fries-regular',
    name: 'KFC Fries regular',
    aliases: ['kfc fries', 'french fries'],
    category: 'snack',
    diet: 'veg',
    chain: true,
    values: [224, 5, 28, 11, 0],
    perServing: 75,
    serving: { g: 75, name: ['pack', 'packs'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'kfc-krisper',
    name: 'KFC Classic Chicken Krisper',
    aliases: ['chicken krisper', 'krisper burger', 'kfc krisper'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [405, 17, 47, 17, 0],
    perServing: 150,
    serving: { g: 150, name: ['burger', 'burgers'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'kfc-popcorn-rice-bowl',
    name: 'KFC Popcorn Chicken Rice Bowl',
    aliases: ['kfc rice bowl', 'popcorn rice bowl'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [615, 24, 88, 19, 0],
    perServing: 340,
    serving: { g: 340, name: ['bowl', 'bowls'] },
    meals: ['lunch', 'dinner'],
  },
  {
    id: 'kfc-chicken-roll-double',
    name: 'KFC Chicken Roll Double',
    aliases: ['kfc roll', 'chicken roll', 'kfc twister'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [526, 23, 51, 27, 0],
    perServing: 190,
    serving: { g: 190, name: ['roll', 'rolls'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'kfc-veg-zinger',
    name: 'KFC Veg Zinger',
    aliases: ['veg zinger', 'kfc veg burger'],
    category: 'snack',
    diet: 'veg',
    chain: true,
    values: [619, 14, 83, 25, 0],
    perServing: 230,
    serving: { g: 230, name: ['burger', 'burgers'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'dominos-peppy-paneer-slice',
    name: "Domino's Peppy Paneer pizza slice",
    aliases: ['peppy paneer', 'dominos paneer pizza', 'pizza slice'],
    category: 'snack',
    diet: 'veg',
    chain: true,
    values: [169, 7.2, 24.8, 4.5, 0],
    perServing: 68,
    serving: { g: 68, name: ['slice', 'slices'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'dominos-margherita-regular-slice',
    name: "Domino's Margherita regular pizza slice",
    aliases: ['dominos margherita', 'margherita slice', 'pizza slice'],
    category: 'snack',
    diet: 'veg',
    chain: true,
    values: [138, 6.8, 20.5, 3.2, 0],
    perServing: 52,
    serving: { g: 52, name: ['slice', 'slices'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'dominos-farmhouse-medium-slice',
    name: "Domino's Farmhouse medium pizza slice",
    aliases: ['dominos farmhouse', 'farmhouse pizza slice', 'pizza slice'],
    category: 'snack',
    diet: 'veg',
    chain: true,
    values: [185, 7.9, 29.3, 4, 0],
    perServing: 83,
    serving: { g: 83, name: ['slice', 'slices'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'dominos-garlic-bread',
    name: "Domino's Garlic Bread",
    aliases: ['garlic breadsticks', 'dominos garlic bread', 'garlic sticks'],
    category: 'snack',
    diet: 'veg',
    chain: true,
    values: [340, 11.9, 57.6, 6.9, 0],
    perServing: 122,
    serving: { g: 122, name: ['serving', 'servings'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'dominos-stuffed-garlic-bread',
    name: "Domino's Stuffed Garlic Bread",
    aliases: ['stuffed garlic bread', 'cheese garlic bread dominos'],
    category: 'snack',
    diet: 'veg',
    chain: true,
    values: [540, 20.2, 82.4, 14.4, 0],
    perServing: 190,
    serving: { g: 190, name: ['piece', 'pieces'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'dominos-zingy-parcel-veg',
    name: "Domino's Zingy Parcel Veg",
    aliases: ['zingy parcel', 'dominos parcel', 'pizza parcel'],
    category: 'snack',
    diet: 'veg',
    chain: true,
    values: [620, 16.9, 69.5, 30.5, 0],
    perServing: 180,
    serving: { g: 180, name: ['parcel', 'parcels'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'bk-crispy-veg',
    name: 'Burger King Crispy Veg',
    aliases: ['crispy veg burger', 'burger king veg', 'bk crispy veg'],
    category: 'snack',
    diet: 'veg',
    chain: true,
    values: [306, 7.1, 47.2, 10, 0],
    perServing: 131,
    serving: { g: 131, name: ['burger', 'burgers'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'bk-paneer-royale',
    name: 'Burger King Paneer Royale',
    aliases: ['paneer royale', 'burger king paneer', 'bk paneer'],
    category: 'paneer',
    diet: 'veg',
    chain: true,
    values: [600, 18.5, 58.1, 33.1, 0],
    perServing: 253,
    serving: { g: 253, name: ['burger', 'burgers'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'bk-fiery-chicken',
    name: 'Burger King Fiery Chicken',
    aliases: ['fiery chicken', 'burger king chicken', 'bk chicken burger'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [588, 16.3, 64, 26.6, 0],
    perServing: 229,
    serving: { g: 229, name: ['burger', 'burgers'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'bk-egg-burger',
    name: 'Burger King BK Egg',
    aliases: ['bk egg', 'burger king egg', 'egg burger'],
    category: 'egg',
    diet: 'egg',
    chain: true,
    values: [250, 10, 27.8, 12.1, 0],
    perServing: 109,
    serving: { g: 109, name: ['burger', 'burgers'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'bk-veg-whopper',
    name: 'Burger King Veg Whopper',
    aliases: ['veg whopper', 'whopper', 'burger king whopper'],
    category: 'snack',
    diet: 'veg',
    chain: true,
    values: [682, 16, 97.1, 25.4, 0],
    perServing: 285,
    serving: { g: 285, name: ['burger', 'burgers'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'subway-veggie-delite-6',
    name: 'Subway Veggie Delite 6 inch',
    aliases: ['veggie delite', 'subway veg sub', 'subway veggie'],
    category: 'snack',
    diet: 'veg',
    chain: true,
    values: [242, 9, 46, 3, 0],
    perServing: 164,
    serving: { g: 164, name: ['6 inch sub', '6 inch subs'] },
    meals: ['lunch', 'dinner'],
  },
  {
    id: 'subway-paneer-tikka-6',
    name: 'Subway Paneer Tikka 6 inch',
    aliases: ['subway paneer tikka', 'paneer tikka sub', 'subway paneer'],
    category: 'paneer',
    diet: 'veg',
    chain: true,
    values: [410, 16, 50, 16, 0],
    perServing: 224,
    serving: { g: 224, name: ['6 inch sub', '6 inch subs'] },
    meals: ['lunch', 'dinner'],
  },
  {
    id: 'subway-tandoori-chicken-tikka-6',
    name: 'Subway Tandoori Chicken Tikka 6 inch',
    aliases: ['subway chicken tikka', 'tandoori chicken tikka sub', 'subway chicken'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [341, 20, 48, 8, 0],
    perServing: 224,
    serving: { g: 224, name: ['6 inch sub', '6 inch subs'] },
    meals: ['lunch', 'dinner'],
  },
  {
    id: 'subway-roasted-chicken-strips-6',
    name: 'Subway Roasted Chicken Strips 6 inch',
    aliases: ['roasted chicken strips sub', 'subway chicken strips', 'subway sub'],
    category: 'chicken',
    diet: 'nonveg',
    chain: true,
    values: [444, 22, 39, 22, 0],
    perServing: 280,
    serving: { g: 280, name: ['6 inch sub', '6 inch subs'] },
    meals: ['lunch', 'dinner'],
  },
  // Indian health drinks, biscuits, chocolates and snacks, per 100 g as printed (FatSecret India, October 2026)
  {
    id: 'bournvita',
    name: 'Cadbury Bournvita (dry powder)',
    aliases: ['bournvita', 'cadbury bournvita', 'health drink'],
    category: 'drink',
    values: [393, 7, 85.2, 1.8, 0],
    amount: 20,
    meals: ['breakfast', 'snacks'],
  },
  {
    id: 'horlicks-classic-malt',
    name: 'Horlicks Classic Malt (dry powder)',
    aliases: ['horlicks', 'classic malt', 'health drink'],
    category: 'drink',
    values: [377, 11, 79, 2, 0],
    amount: 20,
    meals: ['breakfast', 'snacks'],
  },
  {
    id: 'horlicks-protein',
    name: 'Horlicks Protein Plus (dry powder)',
    aliases: ['horlicks protein', 'protein plus', 'health drink'],
    category: 'drink',
    values: [370, 33, 55, 2, 0],
    amount: 30,
    meals: ['breakfast', 'snacks'],
  },
  {
    id: 'protinex-original',
    name: 'Protinex Original (dry powder)',
    aliases: ['protinex', 'protein powder'],
    category: 'drink',
    values: [350, 32, 54.5, 0.5, 0],
    amount: 30,
    meals: ['breakfast', 'snacks'],
  },
  {
    id: 'complan-chocolate',
    name: 'Complan Chocolate (dry powder)',
    aliases: ['complan', 'health drink'],
    category: 'drink',
    values: [428, 18, 62, 11, 0],
    amount: 30,
    meals: ['breakfast', 'snacks'],
  },
  {
    id: 'cerelac',
    name: 'Nestle Cerelac (dry)',
    aliases: ['cerelac', 'baby cereal', 'baby food', 'infant cereal'],
    category: 'breakfast',
    values: [415, 15, 68.5, 9, 0],
    amount: 30,
    meals: ['breakfast', 'snacks'],
  },
  {
    id: 'britannia-marie-gold',
    name: 'Britannia Marie Gold',
    aliases: ['marie gold', 'marie biscuit', 'britannia marie', 'biscuit'],
    category: 'snack',
    values: [445, 7.9, 79.5, 10.6, 0],
    amount: 30,
    meals: ['snacks', 'breakfast'],
  },
  {
    id: 'britannia-good-day',
    name: 'Britannia Good Day Cashew',
    aliases: ['good day', 'britannia good day', 'cookies', 'biscuit'],
    category: 'snack',
    values: [512, 7, 67, 24, 0],
    amount: 30,
    meals: ['snacks', 'breakfast'],
  },
  {
    id: 'cadbury-dairy-milk',
    name: 'Cadbury Dairy Milk',
    aliases: ['dairy milk', 'cadbury', 'chocolate', 'milk chocolate bar'],
    category: 'snack',
    values: [524, 6.8, 62.8, 27.7, 0],
    amount: 25,
    meals: ['snacks'],
  },
  {
    id: 'nestle-kitkat',
    name: 'Nestle KitKat',
    aliases: ['kitkat', 'kit kat', 'chocolate wafer'],
    category: 'snack',
    values: [481, 7.2, 51.8, 27.3, 0],
    amount: 20,
    meals: ['snacks'],
  },
  {
    id: 'snickers-bar',
    name: 'Snickers',
    aliases: ['snickers bar', 'chocolate bar'],
    category: 'snack',
    values: [491, 8.1, 60.2, 24.2, 0],
    amount: 50,
    meals: ['snacks'],
  },
  {
    id: 'kurkure-masala-munch',
    name: 'Kurkure',
    aliases: ['kurkure', 'masala munch', 'namkeen', 'chips'],
    category: 'snack',
    values: [556, 6.6, 56, 34, 0],
    amount: 30,
    meals: ['snacks'],
  },
  {
    id: 'haldiram-moong-dal',
    name: "Haldiram's Moong Dal",
    aliases: ['haldiram moong dal', 'fried moong dal', 'namkeen'],
    category: 'snack',
    values: [479, 21.65, 52.89, 20.14, 0],
    amount: 30,
    meals: ['snacks'],
  },
  {
    id: 'amul-cheese-slice',
    name: 'Amul Cheese Slice',
    aliases: ['cheese slice', 'amul cheese', 'processed cheese'],
    category: 'dairy',
    values: [311, 20, 1.5, 25, 0],
    amount: 20,
    meals: ['breakfast', 'snacks', 'lunch'],
  },
  // Indian drinks, per pack as sold (values per 100 ml scaled to the pack)
  {
    id: 'thums-up',
    name: 'Thums Up',
    aliases: ['thums up', 'thumbs up', 'cola', 'soft drink', 'coke'],
    category: 'drink',
    values: [106, 0, 26.5, 0, 0],
    perServing: 250,
    serving: { g: 250, name: ['bottle (250 ml)', 'bottles (250 ml)'] },
    meals: ['snacks', 'lunch', 'dinner'],
  },
  {
    id: 'frooti',
    name: 'Frooti Mango Drink',
    aliases: ['frooti', 'mango drink', 'packaged juice'],
    category: 'drink',
    values: [130, 0, 32.4, 0, 0],
    perServing: 200,
    serving: { g: 200, name: ['pack (200 ml)', 'packs (200 ml)'] },
    meals: ['snacks'],
  },
  {
    id: 'maaza',
    name: 'Maaza Mango Drink',
    aliases: ['maaza', 'mango drink', 'packaged juice'],
    category: 'drink',
    values: [96, 0, 24, 0, 0],
    perServing: 200,
    serving: { g: 200, name: ['bottle (200 ml)', 'bottles (200 ml)'] },
    meals: ['snacks'],
  },
  {
    id: 'real-mixed-fruit-juice',
    name: 'Real Mixed Fruit Juice',
    aliases: ['real juice', 'real fruit juice', 'packaged juice', 'tropicana'],
    category: 'drink',
    values: [112, 0.8, 28, 0, 0],
    perServing: 200,
    serving: { g: 200, name: ['glass (200 ml)', 'glasses (200 ml)'] },
    meals: ['breakfast', 'snacks'],
  },
  {
    id: 'amul-kool-flavoured-milk',
    name: 'Amul Kool Flavoured Milk',
    aliases: ['amul kool', 'flavoured milk', 'kool'],
    category: 'drink',
    values: [178, 6.4, 24, 6.2, 0],
    perServing: 200,
    serving: { g: 200, name: ['bottle (200 ml)', 'bottles (200 ml)'] },
    meals: ['snacks', 'breakfast'],
  },
  {
    id: 'amul-taaza-toned-milk',
    name: 'Amul Taaza Toned Milk',
    aliases: ['amul taaza', 'toned milk', 'packet milk'],
    category: 'dairy',
    values: [116, 6, 9.4, 6, 0],
    perServing: 200,
    serving: { g: 200, name: ['glass (200 ml)', 'glasses (200 ml)'] },
    meals: ['breakfast', 'snacks', 'dinner'],
  },
  // Pea protein, casein, mass gainers and amino acids (one scoop as on the label)
  {
    id: 'nutrabay-pea-protein-isolate',
    name: 'Nutrabay Pure Pea Protein Isolate',
    aliases: ['pea protein', 'nutrabay pea', 'vegan protein', 'pea isolate'],
    category: 'drink',
    values: [117, 25.3, 2.8, 0.5, 0],
    perServing: 40,
    serving: { g: 40, name: ['scoop', 'scoops'] },
  },
  {
    id: 'nutrabay-gold-hydrolyzed-pea-protein',
    name: 'Nutrabay Gold Hydrolyzed Pea Protein',
    aliases: ['pea protein', 'hydrolyzed pea', 'nutrabay gold pea', 'vegan protein'],
    category: 'drink',
    values: [135, 25, 9.88, 0.68, 0],
    perServing: 40,
    serving: { g: 40, name: ['scoop', 'scoops'] },
  },
  {
    id: 'nakpro-pea-protein-isolate',
    name: 'Nakpro Pea Protein Isolate',
    aliases: ['pea protein', 'nakpro pea', 'vegan protein', 'pea isolate'],
    category: 'drink',
    values: [139, 28.8, 1.08, 2.16, 0],
    perServing: 36,
    serving: { g: 36, name: ['scoop', 'scoops'] },
  },
  {
    id: 'myprotein-pea-protein-isolate',
    name: 'Myprotein Pea Protein Isolate',
    aliases: ['pea protein', 'myprotein pea', 'vegan protein', 'pea isolate'],
    category: 'drink',
    values: [107, 23, 0.9, 1.5, 0],
    perServing: 30,
    serving: { g: 30, name: ['scoop', 'scoops'] },
  },
  {
    id: 'muscleblaze-raw-whey',
    name: 'MuscleBlaze Raw Whey Protein',
    aliases: ['raw whey', 'muscleblaze raw whey', 'whey concentrate'],
    category: 'drink',
    values: [118, 24, 2.1, 1.5, 0],
    perServing: 30,
    serving: { g: 30, name: ['scoop', 'scoops'] },
  },
  {
    id: 'on-gold-standard-casein',
    name: 'Optimum Nutrition Gold Standard 100% Casein',
    aliases: ['casein', 'on casein', 'micellar casein', 'night protein'],
    category: 'drink',
    values: [120, 24, 3, 1, 0],
    perServing: 34,
    serving: { g: 34, name: ['scoop', 'scoops'] },
  },
  {
    id: 'muscleblaze-micellar-casein',
    name: 'MuscleBlaze Micellar Casein',
    aliases: ['casein', 'muscleblaze casein', 'micellar casein', 'night protein'],
    category: 'drink',
    values: [120, 24, 5.7, 0.17, 0],
    perServing: 35,
    serving: { g: 35, name: ['scoop', 'scoops'] },
  },
  {
    id: 'muscleblaze-mass-gainer-xxl',
    name: 'MuscleBlaze Mass Gainer XXL (powder)',
    aliases: ['mass gainer', 'weight gainer', 'muscleblaze gainer', 'gainer'],
    category: 'drink',
    values: [374, 20, 70, 2, 0],
    amount: 100,
  },
  {
    id: 'muscleblaze-high-protein-gainer',
    name: 'MuscleBlaze High Protein Gainer (powder)',
    aliases: ['mass gainer', 'high protein gainer', 'weight gainer', 'gainer'],
    category: 'drink',
    values: [386, 30, 59, 4, 0],
    amount: 100,
  },
  {
    id: 'muscleblaze-creamp-creatine',
    name: 'MuscleBlaze Creamp Creatine Monohydrate',
    aliases: ['creatine', 'creatine monohydrate', 'creamp'],
    category: 'drink',
    values: [0, 0, 0, 0, 0],
    perServing: 3,
    serving: { g: 3, name: ['scoop (3 g)', 'scoops (3 g)'] },
    meals: ['breakfast', 'snacks'],
  },
  {
    id: 'gnc-bcaa',
    name: 'GNC BCAA',
    aliases: ['bcaa', 'amino acids', 'gnc bcaa'],
    category: 'drink',
    values: [8, 0, 2, 0, 0],
    perServing: 13.25,
    serving: { g: 13.25, name: ['scoop', 'scoops'] },
    meals: ['breakfast', 'snacks'],
  },
];

export function fromLabel(l: LabelDef): Food {
  const [kcal, proteinG, carbsG, fatG, fibreG] = l.values;
  const factor = l.perServing ? 100 / l.perServing : 1;
  const unit = l.serving ? 'piece' : 'g';
  return {
    id: l.id,
    name: l.name,
    aliases: l.aliases ?? [],
    category: l.category,
    region: 'all',
    meals: l.meals ?? ALL_MEALS,
    diet: l.diet ?? 'veg',
    jain: false,
    per100: scaleNutrients({ kcal, proteinG, carbsG, fatG, fibreG }, factor),
    fatPer100: null,
    units: unit === 'piece' ? ['piece', 'g'] : ['g'],
    defaultAmount: l.amount && unit === 'g' ? l.amount : 1,
    density: 1,
    pieceG: l.serving?.g ?? null,
    pieceName: l.serving?.name ?? null,
    servingG: null,
    source: l.chain ? 'chain' : 'label',
    recipe: null,
  };
}

const NONVEG = new Set<IngredientId>([
  'chicken',
  'chicken-breast-cooked',
  'mutton',
  'fish',
  'prawns',
  'tuna-canned',
  'salmon',
  'pork-belly',
  'bacon',
  'beef-mince',
  'mutton-mince',
  'chicken-drumstick',
  'chicken-thigh',
  'chicken-wing',
  'mussels',
  'bombay-duck',
  'mackerel',
  'sardine',
  'pomfret',
  'seer-fish',
  'hilsa',
  'catla',
  'rohu',
  'bhetki',
  'karimeen',
  'tuna-fresh',
  'anchovy',
  'basa',
  'tilapia',
  'catfish',
  'mullet',
  'red-snapper',
  'crab',
  'lobster',
  'squid',
  'clams',
  'oyster',
  'prawns-small',
  'beef',
  'pork',
  'duck',
  'quail',
  'turkey',
  'rabbit',
  'goat-liver',
  'goat-brain',
  'goat-tripe',
  'chicken-liver',
  'chicken-gizzard',
  'country-chicken',
]);
/** Jain food also leaves out onion, garlic and root vegetables. */
const NOT_JAIN = new Set<IngredientId>([
  ...NONVEG,
  'egg',
  'egg-white',
  'sweet-potato-baked',
  'onion',
  'garlic',
  'ginger',
  'potato',
  'carrot',
  'radish',
  'beetroot',
  'sweet-potato',
  'colocasia',
  'yam',
  'turnip',
  'lotus-stem',
  'shallots',
  'leek',
]);

const lookup = (id: string): Ingredient => {
  const ing = (INGREDIENTS as Record<string, Ingredient>)[id];
  if (!ing) throw new Error(`Unknown ingredient ${id}`);
  return ing;
};

function dietOf(ids: IngredientId[]): Food['diet'] {
  if (ids.some((i) => NONVEG.has(i))) return 'nonveg';
  if (ids.includes('egg') || ids.includes('egg-white')) return 'egg';
  return 'veg';
}

export function fromDish(d: DishDef): Food {
  const lines = d.lines.map(([id, grams]) => ({ id, grams }));
  const cookedG =
    typeof d.cooked === 'number' ? d.cooked : cookedFromMoisture(lines, lookup, d.cooked.moisture);
  const total = sumNutrients(lines.map((l) => ingredientNutrients(lookup(l.id), l.grams)));
  const fatLines = lines.filter((l) => d.fat?.includes(l.id as IngredientId));
  const fatTotal = sumNutrients(fatLines.map((l) => ingredientNutrients(lookup(l.id), l.grams)));
  const per = 100 / cookedG;
  const ids = d.lines.map(([id]) => id);
  return {
    id: d.id,
    name: d.name,
    aliases: d.aliases ?? [],
    category: d.category,
    region: d.region ?? 'north',
    meals: d.meals,
    diet: dietOf(ids),
    jain: !ids.some((i) => NOT_JAIN.has(i)),
    per100: scaleNutrients(total, per),
    fatPer100: fatLines.length ? scaleNutrients(fatTotal, per) : null,
    units: d.units,
    defaultAmount: d.amount ?? 1,
    density: d.density ?? 1,
    pieceG: d.pieces ? cookedG / d.pieces : null,
    pieceName: d.piece ?? null,
    servingG: d.servings ? cookedG / d.servings : null,
    source: 'recipe',
    recipe: {
      lines: lines.map((l) => ({ name: lookup(l.id).name, grams: l.grams })),
      cookedG,
      makes: d.pieces
        ? `${d.pieces} ${d.piece?.[d.pieces > 1 ? 1 : 0] ?? 'pieces'}`
        : `${d.servings ?? 1} servings`,
    },
  };
}

function fromBasic(b: BasicDef): Food {
  const ing = lookup(b.ingredient);
  return {
    id: b.id,
    name: b.name,
    aliases: b.aliases ?? [],
    category: b.category,
    region: b.region ?? 'all',
    meals: b.meals,
    diet: dietOf([b.ingredient]),
    jain: !NOT_JAIN.has(b.ingredient),
    per100: {
      kcal: ing.kcal,
      proteinG: ing.proteinG,
      carbsG: ing.carbsG,
      fatG: ing.fatG,
      fibreG: ing.fibreG,
    },
    fatPer100: null,
    units: b.units,
    defaultAmount: b.amount ?? 1,
    density: b.density ?? 1,
    pieceG: b.pieceG ?? null,
    pieceName: b.piece ?? null,
    servingG: null,
    source: 'ingredient',
    recipe: null,
  };
}

/** Every built-in food, resolved to per 100 g. */
export const FOODS: Food[] = [
  ...DISHES.map(fromDish),
  ...BASICS.map(fromBasic),
  ...LABELS.map(fromLabel),
].map(withMeasures);
export const FOOD_BY_ID = new Map(FOODS.map((f) => [f.id, f]));

/** Ingredients a person can use in their own recipes. */
export const RECIPE_INGREDIENTS = Object.entries(INGREDIENTS)
  .filter(([id]) => id !== 'water')
  .map(([id, ing]) => ({ id: id as IngredientId, ...(ing as Ingredient) }));

/** The few foods offered first for each meal before the person has history. */
export const STARTER_FOODS: Record<Meal, string[]> = {
  breakfast: [
    'chai',
    'poha',
    'aloo-paratha',
    'idli',
    'omelette',
    'boiled-egg',
    'oats-milk',
    'protein-shake',
    'milk',
    'peanut-butter-toast',
  ],
  lunch: [
    'roti',
    'dal-tadka',
    'rice',
    'rajma',
    'chole',
    'curd',
    'chicken-breast',
    'aloo-gobhi',
    'sambar',
  ],
  snacks: [
    'chai',
    'banana',
    'protein-shake',
    'roasted-makhana',
    'peanuts',
    'roasted-chana',
    'greek-yogurt',
    'samosa',
    'apple',
  ],
  dinner: [
    'roti',
    'dal-tadka',
    'paneer-bhurji',
    'chicken-curry',
    'soya-chunks-boiled',
    'rice',
    'mix-veg',
    'curd',
  ],
};

/** A person's own food or recipe, as a food the logger can use. */
export function customToFood(c: CustomFood): Food {
  const servingG = c.kind === 'recipe' && c.cookedG && c.servings ? c.cookedG / c.servings : null;
  return {
    id: c.id,
    name: c.name,
    aliases: [],
    category: 'basic',
    region: 'all',
    meals: ['breakfast', 'lunch', 'snacks', 'dinner'],
    diet: c.diet,
    jain: false,
    per100: {
      kcal: c.kcal,
      proteinG: c.proteinG,
      carbsG: c.carbsG,
      fatG: c.fatG,
      fibreG: c.fibreG,
    },
    fatPer100: null,
    units: servingG ? ['share', 'katori', 'g'] : c.pieceG ? ['piece', 'g'] : ['g', 'katori'],
    defaultAmount: servingG || c.pieceG ? 1 : 100,
    density: 1,
    pieceG: c.pieceG,
    // A food of their own is counted in servings of the weight they gave, not "pieces".
    pieceName: c.kind === 'food' && c.pieceG ? ['serving', 'servings'] : null,
    servingG,
    source: c.kind === 'recipe' ? 'custom_recipe' : 'custom',
    recipe:
      c.kind === 'recipe' && c.ingredients && c.cookedG
        ? {
            lines: c.ingredients.map((l) => ({
              name: (INGREDIENTS as Record<string, Ingredient>)[l.id]?.name ?? l.id,
              grams: l.grams,
            })),
            cookedG: c.cookedG,
            makes: `${c.servings ?? 1} shares`,
          }
        : null,
  };
}

/**
 * Per 100 g of a person's own recipe from its ingredients and the pot's cooked weight. With no
 * cooked weight, the raw weight is used (nothing gained or lost in cooking).
 */
export function recipePer100(lines: { id: string; grams: number }[], cookedG: number | null) {
  const total = sumNutrients(lines.map((l) => ingredientNutrients(lookup(l.id), l.grams)));
  const raw = lines.reduce((s, l) => s + l.grams, 0);
  const weight = cookedG ?? raw;
  return { per100: scaleNutrients(total, 100 / weight), cookedG: weight, rawG: raw };
}

/** Diet of a recipe from its ingredients. */
export const recipeDiet = (ids: string[]) => dietOf(ids as IngredientId[]);
